// Gallery — lists the signed-in user's saved posts and lets them download
// or delete each one. Reads from the `posts` table + `gallery` storage
// bucket; Row Level Security limits queries to auth.uid()'s rows.
//
// Images are served via short-lived signed URLs (private bucket = no
// anonymous reads), generated in parallel on first list and cached for the
// page's lifetime. URLs expire after 1h; if the user keeps the tab open
// longer than that, a hard refresh re-signs them.
//
// AuthGate wraps this route in router/config.tsx, so we never see an
// unauthenticated user here.

import { useState, useEffect, useCallback, useMemo, useRef, useSyncExternalStore } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AppLayoutV2 from '@/components/feature/AppLayoutV2';
import {
  type Post,
  listPosts,
  deletePost,
  getSignedImageUrl,
  getSignedImageUrls,
  unlockPost,
  unlockAllBatch,
  setPostStarred,
} from '@/lib/gallery';
import {
  editCohortTiles,
  fetchScheduledByPost,
  summarisePostSchedule,
  type PostScheduleStatus,
} from '@/lib/scheduledByPost';
import type { ScheduledPost } from '@/lib/api';
import {
  getBrandSetupStatus,
  type BrandSetupStatusResult,
  adStrategistTopUp20,
  cancelAdStrategistTopUp,
  finalizeStaleTopUp,
  finalizeBrandSetupStale,
  getAdStrategistJobRenders,
  type AdConceptRenderRow,
  InsufficientCreditsError,
  getPublishJob,
} from '@/lib/api';
import PostToSocialModal, { type SocialChannel } from '@/pages/gallery/components/PostToSocialModal';
import ScheduleModal from '@/pages/schedule/components/ScheduleModal';
import ProductCatalogPicker, {
  type ProductPickerSelection,
} from '@/components/feature/ProductCatalogPicker';
import { PLATFORM_META } from '@/pages/schedule/components/platforms';
import { useBrand } from '@/hooks/useBrand';
import { useCredits } from '@/hooks/useCredits';
import { useFacebookConnection } from '@/hooks/useFacebookConnection';
import { useSocialPublishingEnabled, useSchedulingEnabled } from '@/hooks/useFeatureFlags';
import CarouselThumbnail from '@/components/feature/CarouselThumbnail';
import ThumbImage from '@/components/feature/ThumbImage';
import { aspectClass } from '@/pages/app/lib/composeRender';
import { AnimatePresence, motion, LayoutGroup } from 'framer-motion';

// Per-plan visibility cap. Posts beyond this index (newest-first) show
// an "Unlock" overlay prompting upgrade. Match backend BRAND_CAPS spirit.
const PLAN_VISIBLE_POSTS: Record<string, number> = {
  free: 4,
  starter: 12,
  pro: Infinity,
  business: Infinity,
};

// Jun 22 — render timeout. After this duration in RENDERING state with
// no completion, tiles surface a Try-again button instead of the
// infinite spinner. Tuned to cover the longest legitimate render
// (gpt-image-2 High + retries ≈ 90s, plus SDK 180s timeout, plus a
// generous safety margin) but short enough that users don't stare at
// stuck tiles forever.
const RENDER_TIMEOUT_MS = 5 * 60 * 1000;

function formatScheduledDate(dateStr: string): string {
  const d = new Date(dateStr);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) {
    return `today ${d.toLocaleTimeString(undefined, { hour: 'numeric', hour12: true }).toLowerCase().replace(' ', '')}`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString(undefined, { hour: 'numeric', hour12: true }).toLowerCase().replace(' ', '');
}

// Channel-display metadata for direct publications (post.published_to).
// Mirrors the brand colors used in the sidebar's Connect group + the
// FB-blue tile hover button so users recognize "this went to Facebook"
// at a glance.
const PUBLICATION_CHANNEL_META: Record<string, { label: string; icon: string; color: string }> = {
  facebook: { label: 'Facebook', icon: 'ri-facebook-circle-fill', color: '#1877F2' },
  instagram: { label: 'Instagram', icon: 'ri-instagram-line', color: '#E4405F' },
  tiktok: { label: 'TikTok', icon: 'ri-tiktok-line', color: '#0F0F0F' },
  // LinkedIn kept for historical published_to entries — won't be reachable
  // for new publications since LinkedIn was dropped from the Connect group
  // on June 7. Removable once any old rows are migrated.
  linkedin: { label: 'LinkedIn', icon: 'ri-linkedin-box-fill', color: '#0A66C2' },
};

/** Compact publication chips — ONE per unique channel, stacked
 *  horizontally. Format: [icon] [check] [date]. Channel name dropped
 *  (icon already conveys it). Each chip is the channel's solid brand
 *  color so 2-3 chips read as a glanceable "this went to FB + IG".
 *
 *  Dedup: multiple publications to the same channel keep ONLY the
 *  latest (most recent posted_at). Order: by latest-posted-at across
 *  channels (most recently posted channel first).
 */
/** Format a USD cost for the red diagnostic pill. Uses 4 decimals below
 *  10 cents (so $0.0672 stays readable) and 2 decimals at/above (so a
 *  carousel total like $1.34 doesn't get noisy trailing zeros). */
function formatCostUsd(value: number): string {
  if (value < 0.10) return `$${value.toFixed(4)}`;
  return `$${value.toFixed(2)}`;
}

/** Red diagnostic pill — top-left of each gallery tile. Shows the
 *  provider that rendered the tile plus what we actually spent on the
 *  underlying API call(s). Hidden when cost is unknown (historical posts
 *  pre-tracking, or paths that don't plumb cost yet). Added June 7, 2026
 *  to surface the Gemini pricing mismatch (docs say $0.067/img, observed
 *  prod is ~$0.50/img); provider segment added later so we can also see
 *  which model rendered each tile at a glance. */
function CostPill({
  cost,
  provider,
}: {
  cost: number | null | undefined;
  provider: string | null | undefined;
}) {
  if (cost == null || !Number.isFinite(cost)) return null;
  const providerLabel = provider ? provider : null;
  return (
    <span
      title={
        providerLabel
          ? `Rendered by ${providerLabel} · API cost: $${cost.toFixed(6)}`
          : `API cost: $${cost.toFixed(6)}`
      }
      className="absolute top-2 left-11 px-2 py-0.5 rounded-md bg-red-600 text-white text-[11px] font-bold shadow-md shadow-red-900/30 tabular-nums"
    >
      {providerLabel ? `${providerLabel} · ` : ''}{formatCostUsd(cost)}
    </span>
  );
}

function PublicationPill({ publications }: { publications: import('@/lib/gallery').PostPublication[] }) {
  if (!publications || publications.length === 0) return null;

  // Group by channel, keeping the latest entry per channel.
  const latestPerChannel = new Map<string, import('@/lib/gallery').PostPublication>();
  for (const pub of publications) {
    const existing = latestPerChannel.get(pub.channel);
    const tNew = pub.posted_at ? new Date(pub.posted_at).getTime() : 0;
    const tOld = existing?.posted_at ? new Date(existing.posted_at).getTime() : 0;
    if (!existing || tNew > tOld) {
      latestPerChannel.set(pub.channel, pub);
    }
  }
  // Sort channel chips newest-posted first.
  const chips = Array.from(latestPerChannel.values()).sort((a, b) => {
    const ta = a.posted_at ? new Date(a.posted_at).getTime() : 0;
    const tb = b.posted_at ? new Date(b.posted_at).getTime() : 0;
    return tb - ta;
  });

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((pub) => {
        const meta = PUBLICATION_CHANNEL_META[pub.channel] ?? {
          label: pub.channel,
          icon: 'ri-send-plane-fill',
          color: '#6b7280',
        };
        const when = pub.posted_at
          ? new Date(pub.posted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          : '';
        return (
          <div
            key={pub.channel}
            title={`Posted to ${meta.label}${when ? ' on ' + when : ''}`}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-bold text-white shadow-md shadow-black/20"
            style={{ backgroundColor: meta.color }}
          >
            <i className={meta.icon} />
            <i className="ri-check-line" />
            {when && <span>{when}</span>}
          </div>
        );
      })}
    </div>
  );
}

/** Small pill on each My Creatives card showing schedule state. Renders nothing
 *  for the 'none' state so unscheduled posts stay visually compact. */
function ScheduleStatusPill({ status }: { status: PostScheduleStatus }) {
  if (status.kind === 'none') return null;

  if (status.kind === 'posted') {
    const when = status.latest.posted_at
      ? new Date(status.latest.posted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : 'recently';
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs">
        <i className="ri-check-double-line" />
        <span className="font-bold">Posted</span>
        <span className="text-emerald-600/80">·</span>
        <PlatformIconRow platforms={status.platforms} />
        <span className="text-emerald-600/80">·</span>
        <span>{when}</span>
      </div>
    );
  }

  // Failed — publish worker tried and got an error from Meta. Tooltip
  // surfaces the error message so the user can diagnose without bouncing
  // to /schedule. Red to signal action needed.
  if (status.kind === 'failed') {
    const errorMsg = status.latest.error || 'Publish failed';
    return (
      <div
        className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs"
        title={errorMsg}
      >
        <i className="ri-error-warning-line" />
        <span className="font-bold">Failed</span>
        <span className="text-red-600/80">·</span>
        <PlatformIconRow platforms={status.platforms} />
        {status.count > 1 && (
          <>
            <span className="text-red-600/80">·</span>
            <span>{status.count} platforms</span>
          </>
        )}
      </div>
    );
  }

  // Scheduled — next upcoming. Translucent white-gray fill + backdrop-blur
  // so the pill stays legible on both dark and light tile imagery; mirrors
  // the date chip in the top-right corner. The primary-colored icon keeps
  // the brand cue without losing contrast.
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/90 backdrop-blur-sm border border-surface-dark/40 text-dark text-xs shadow-sm">
      <i className="ri-calendar-event-line text-primary" />
      <span className="font-bold">Scheduled</span>
      <span className="text-dark-muted/60">·</span>
      <PlatformIconRow platforms={status.platforms} />
      <span className="text-dark-muted/60">·</span>
      <span className="text-dark-muted">{formatScheduledDate(status.next.scheduled_at)}</span>
      {status.count > 1 && (
        <span
          className="text-dark-muted"
          title={`${status.count} upcoming schedules across ${status.platforms.length} platforms`}
        >
          +{status.count - 1} more
        </span>
      )}
    </div>
  );
}

// SectionHero — collapsed-section hero card. Shows the section's top 4
// thumbnails in a fan layout that subtly spreads on hover (Apple Photos
// memories feel). Click anywhere on the card → expand the section back
// to the full grid (handled by the parent onClick).
//
// Layout: large 16:10 backdrop with the 4 thumbnails stacked at angles
// in the top-right; left side gets the title/subtitle treatment. On hover
// the fan opens further outward + scales slightly. CSS-only animation
// (no React state) for snappiness.
// Per-tone gradient classes for the hero backdrop. Each section tone gets a
// visually distinct gradient so AS / setup / morning / afternoon / evening
// / tonight cards are distinguishable at a glance even though they share
// the same outer white-card chrome.
const HERO_GRADIENTS: Record<string, string> = {
  as:           'from-primary-light via-white to-accent-light/40',
  setup:        'from-amber-50 via-white to-orange-50',
  // Date / time-of-day buckets (loose tone). The key includes the
  // sub-bucket id so morning/afternoon/evening/tonight each get their
  // own gradient.
  'loose:morning':   'from-yellow-50 via-white to-amber-100/60',
  'loose:afternoon': 'from-sky-50 via-white to-blue-100/40',
  'loose:evening':   'from-orange-50 via-white to-rose-100/40',
  'loose:tonight':   'from-indigo-100/40 via-white to-violet-100/40',
  loose:         'from-surface via-white to-surface-dark/30',
};

function SectionHero({
  posts,
  totalCount,
  imageUrls,
  gradientClass,
}: {
  posts: Post[];
  totalCount: number;
  imageUrls: Record<string, string>;
  gradientClass?: string;
}) {
  // Fan offsets — base positions (rest state) then spread further on hover.
  // 4 thumbnails at slight rotations creating a "deck of cards" look.
  const fans = [
    { rotate: -8,  x: -28, y: 4,  z: 1 },
    { rotate: -3,  x: -10, y: 1,  z: 2 },
    { rotate:  3,  x:  10, y: 1,  z: 3 },
    { rotate:  8,  x:  28, y: 4,  z: 4 },
  ];
  const overflow = totalCount - posts.length;

  return (
    // Fixed pixel height (not aspect-ratio) — the wall is full-width up to
    // ~1700px, so aspect-[16/7] produced a ~700px monster card. 144px is
    // the sweet spot: hero feels like a row, not a hero banner.
    //
    // Layout: text + fan deck sit in ONE left-aligned flex row so they
    // read as a connected unit. The right ~60% of the strip is the
    // blurred ambient backdrop — feels intentional, not empty.
    <div
      className={`relative w-full h-36 rounded-2xl overflow-hidden bg-gradient-to-br ${gradientClass || 'from-surface via-white to-surface-dark/30'} border border-surface-dark/30 group-hover:border-primary/40 transition-colors`}
    >
      {/* Ambient backdrop from first post — blurred heavily so it reads
          as a mood, not a duplicate. */}
      {posts[0] && imageUrls[posts[0].id] && (
        <div
          className="absolute inset-0 opacity-25 blur-3xl scale-110"
          style={{
            backgroundImage: `url(${imageUrls[posts[0].id]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}

      {/* Single left-aligned group: copy + fan deck. They're adjacent so
          the text reads as a label for the deck below/beside it. */}
      <div className="relative z-10 h-full flex items-center gap-6 lg:gap-8 px-5 lg:px-6">
        <div className="flex-shrink-0">
          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-dark-muted mb-1">
            Tap to open
          </p>
          <p className="font-display font-bold text-dark text-xl lg:text-2xl leading-none">
            {totalCount} {totalCount === 1 ? 'creative' : 'creatives'}
          </p>
          <p className="text-xs text-dark-muted mt-1.5 leading-snug">
            {posts.length > 0
              ? 'Hover to preview · click to expand'
              : 'No images to preview yet.'}
          </p>
        </div>

        {posts.length > 0 && (
          <div className="relative w-[200px] h-[120px] flex-shrink-0 pointer-events-none">
            {posts.map((post, i) => {
              const fan = fans[i] || fans[fans.length - 1];
              const url = imageUrls[post.id];
              return (
                // Jun 26 — fan-spread bug fix. Previously a SECOND inner
                // `<div>` carried the hover translate/rotate, but it was
                // `absolute inset-0` so it just slid the image WITHIN
                // the white card frame; the card itself (border + shadow)
                // didn't move. Now the outer card's transform string is
                // built from CSS vars, with separate hover vars that
                // `group-hover` swaps in. The card itself fans outward
                // on hover — image and frame travel together.
                <div
                  key={post.id}
                  className="
                    absolute top-1/2 left-1/2 w-24 h-24 -mt-12 -ml-12
                    rounded-xl overflow-hidden border-[3px] border-white shadow-lg
                    transition-transform duration-300 ease-out
                    [transform:translate(var(--rest-x),var(--rest-y))_rotate(var(--rest-r))]
                    group-hover:[transform:translate(var(--hover-x),var(--hover-y))_rotate(var(--hover-r))_scale(1.05)]
                  "
                  style={{
                    // @ts-expect-error custom CSS vars on style
                    '--rest-x': `${fan.x}px`,
                    '--rest-y': `${fan.y}px`,
                    '--rest-r': `${fan.rotate}deg`,
                    // Hover values — exaggerate the rest fan so cards
                    // spread visibly outward without losing the deck feel.
                    '--hover-x': `${fan.x * 1.6}px`,
                    '--hover-y': `${fan.y * 1.6}px`,
                    '--hover-r': `${fan.rotate * 1.5}deg`,
                    zIndex: fan.z,
                  }}
                >
                  {url ? (
                    <ThumbImage src={url} alt={post.title || ''} />
                  ) : (
                    <div className="absolute inset-0 bg-surface flex items-center justify-center text-dark-muted">
                      <i className="ri-image-line text-xl" />
                    </div>
                  )}
                </div>
              );
            })}
            {overflow > 0 && (
              <div
                className="absolute -bottom-1 -right-1 px-2.5 py-1 rounded-full bg-dark text-white text-[11px] font-bold shadow-md"
                style={{ zIndex: 10 }}
              >
                +{overflow} more
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PlatformIconRow({ platforms }: { platforms: string[] }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {platforms.slice(0, 4).map((p) => {
        const m = PLATFORM_META[p] || PLATFORM_META.default;
        return (
          <i
            key={p}
            className={m.icon}
            style={{ color: m.color, fontSize: '12px' }}
            title={m.label}
          />
        );
      })}
    </span>
  );
}

// ── Module-level gallery cache (Jun 30). Survives in-app route changes (the
// module stays loaded); cleared only on a full reload. Lets navigate-away-and-
// back paint the EXISTING gallery instantly instead of a full-page "Loading…"
// takeover while a silent re-fetch runs in the background. Brand-scoped so
// switching brands never flashes the wrong brand's tiles (brand switch hard-
// reloads anyway, so this is effectively one brand per page load).
type GalleryCacheShape = {
  brandId: string | null;
  posts: Post[];
  imageUrls: Record<string, string>;
  scheduledByPost: Record<string, ScheduledPost[]>;
};
let galleryCache: GalleryCacheShape | null = null;

// ── Module-level "Generate 20 more" controller (Jun 30). The top-up lifecycle
// (prep → rendering → done) lives OUTSIDE the React component so it survives
// navigating away mid-flight: the in-flight POST + resolved job id are captured
// here, not in component state that unmounts and loses everything. The
// component subscribes via useSyncExternalStore. sessionStorage mirrors only
// the RENDER phase (we have a job id to resume polling) so a full reload during
// rendering recovers too; a bare 'prep' (no job id yet) can't be recovered
// after a hard reload, so it's deliberately not persisted — the user just sees
// the idle CTA and the 20 still land in the gallery via source_job_id.
type TopUpStatus = 'idle' | 'prep' | 'rendering' | 'done';
type TopUpController = { status: TopUpStatus; jobId: string | null; total: number; startedAt: number };
const TOPUP_SS_KEY = 'as_topup_active';
const topUpIdle: TopUpController = { status: 'idle', jobId: null, total: 0, startedAt: 0 };
function readTopUpSession(): TopUpController {
  try {
    const raw = sessionStorage.getItem(TOPUP_SS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<TopUpController>;
      if (p.status === 'rendering' && p.jobId) {
        return { status: 'rendering', jobId: p.jobId, total: p.total || 0, startedAt: p.startedAt || 0 };
      }
    }
  } catch { /* sessionStorage unavailable (private mode etc.) */ }
  return topUpIdle;
}
let topUpController: TopUpController = readTopUpSession();
const topUpListeners = new Set<() => void>();
function subscribeTopUp(cb: () => void) {
  topUpListeners.add(cb);
  return () => { topUpListeners.delete(cb); };
}
function getTopUpController() { return topUpController; }
function setTopUpController(patch: Partial<TopUpController>) {
  topUpController = { ...topUpController, ...patch };
  try {
    if (topUpController.status === 'idle') sessionStorage.removeItem(TOPUP_SS_KEY);
    else sessionStorage.setItem(TOPUP_SS_KEY, JSON.stringify(topUpController));
  } catch { /* ignore */ }
  topUpListeners.forEach((fn) => fn());
}

export default function GalleryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeBrand } = useBrand();
  const SOCIAL_PUBLISHING_ENABLED = useSocialPublishingEnabled();
  const SCHEDULING_ENABLED = useSchedulingEnabled();
  const { balance, planTier, refresh: creditsRefresh } = useCredits();
  const visibleCap = PLAN_VISIBLE_POSTS[planTier] ?? PLAN_VISIBLE_POSTS.free;
  // Module-cache hit for THIS brand (Jun 30) — drives instant paint on
  // nav-away-and-back. Read once at mount via the lazy initializers below.
  const cacheHit = !!galleryCache && galleryCache.brandId === (activeBrand?.id ?? null);
  const [posts, setPosts] = useState<Post[]>(() => (cacheHit ? galleryCache!.posts : []));
  // My Creatives filter (Jul 20) — All | Starred. Starred shows only the
  // ads the user has ★-shortlisted.
  const [galleryFilter, setGalleryFilter] = useState<'all' | 'starred'>('all');
  // Group-by-date toggle (Jul 20). ON = per-date section cards (default).
  // OFF = all saved creatives collapsed into a single "All creatives" panel.
  const [groupByDate, setGroupByDate] = useState(true);
  // "Load older creatives" (Jul 1) — the gallery list is capped at 200
  // per page. hasMore = the last page came back full (200), so older
  // creatives exist beyond what's loaded. loadingOlder = a fetch is in
  // flight. olderLoadedRef = the user has paged past the first 200, so
  // fetchPosts must MERGE the fresh head instead of replacing (else a
  // poll/focus-refetch wipes the loaded-older tiles).
  const [hasMore, setHasMore] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const olderLoadedRef = useRef<boolean>(cacheHit ? galleryCache!.posts.length > 200 : false);
  // Sticky bucket assignment, persisted across page refreshes via
  // localStorage. Each post id is bucketed ONCE when first observed
  // (rendered vs locked-stub), then never moves — not on Unlock, not
  // on refresh. Why:
  //   - Unlock click → post.image_path goes from "" → set
  //   - Without sticky buckets: tile transitions olderLocked→olderRendered
  //     → items[] index changes → round-robin column reassignment
  //     → DOM node mounts in a different column → JUMP
  //   - With sticky (in-memory only): jump avoided mid-session, but page
  //     refresh re-classifies the post as 'rendered' → it migrates to a
  //     different spot anyway → user perceives this as "tile moved to
  //     random spot after refresh"
  // localStorage persistence makes the bucket truly stable forever
  // (per-user, per-browser). Entries auto-prune when posts disappear
  // from the fetched list (delete, brand switch).
  // ── Sticky-position tile layout (June 9 rewrite) ──────────────────────
  // Every tile gets a NUMERIC position assigned the first time we see it.
  // Once assigned, the position NEVER changes — not when a stub flips to
  // rendered, not when the backend re-orders rows on the next poll, not
  // when the user clears localStorage on another tab, not even after a
  // brand switch + back. The position is persisted to localStorage so it
  // survives page refreshes.
  //
  // Within a section, tiles are sorted by position ASC. Round-robin
  // distribution into masonry columns therefore puts tile T at the SAME
  // column index forever (idx = positionRank % columnCount). DOM nodes
  // mount in the same column run; React reconciles by post.id; no tile
  // ever teleports.
  //
  // This REPLACES the old `postBucketRef` (rendered/locked sticky bucket).
  // That older approach only pinned the rendered-vs-locked partition; it
  // didn't pin individual positions WITHIN those partitions, so same-tx
  // batched inserts (which share Postgres now() timestamps) still flipped
  // their relative order on every poll. Combined with the backend's
  // `ORDER BY created_at DESC` non-deterministic-on-ties result set, that
  // produced the "tiles move randomly every 4 seconds" behaviour the user
  // has been frustrated by for a week.
  //
  // First-assignment ordering rules:
  //   1. Active AS batch immediate ids — assigned in their backend order
  //      (so the 5 renders cluster at the top-left of the section)
  //   2. Active AS batch stub ids — assigned in their backend order
  //      (so the 15 stubs fill the rest in their original concept order)
  //   3. Everything else — assigned in posts[] order, which is now
  //      deterministic thanks to the backend's (created_at DESC, id ASC)
  //      tiebreak
  // Storage key scoped by brand_id so each brand gets its own assignment
  // map (matches the old bucket-key pattern).
  const positionStorageKey = `gallery_post_positions_v1:${activeBrand?.id ?? 'none'}`;
  const postPositionRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    try {
      const raw = localStorage.getItem(positionStorageKey);
      if (!raw) { postPositionRef.current = new Map(); return; }
      const obj = JSON.parse(raw) as Record<string, number>;
      const map = new Map<string, number>();
      for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'number' && Number.isFinite(v)) map.set(k, v);
      }
      postPositionRef.current = map;
    } catch {
      postPositionRef.current = new Map();
    }
  }, [positionStorageKey]);
  const persistPositions = () => {
    try {
      const obj: Record<string, number> = {};
      postPositionRef.current.forEach((v, k) => { obj[k] = v; });
      localStorage.setItem(positionStorageKey, JSON.stringify(obj));
    } catch { /* quota / private mode — ref still works in-memory */ }
  };
  // Active brand-setup job — populated from sessionStorage after the wizard
  // hands off control. Drives the skeleton placeholder tiles at top of the
  // grid so users see "renders in progress" before the posts list catches up.
  const [activeJob, setActiveJob] = useState<BrandSetupStatusResult | null>(null);
  // Sticky error banner — set when a terminated job had a non-empty
  // `error` field. Lives on AFTER activeJob is cleared so the user
  // sees what went wrong instead of just an empty grid. Dismissable.
  const [jobErrorBanner, setJobErrorBanner] = useState<string | null>(null);
  // Lazy-init from sessionStorage so the very first render already knows
  // a brand-setup job is in flight. Without this, the wizard→gallery
  // hand-off flashes the "No saved posts yet — Create Your First Post"
  // empty state because the brand-scoped useEffect that reads
  // sessionStorage hasn't fired yet on first paint.
  const [activeJobTarget, setActiveJobTarget] = useState<number>(() => {
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('brand_setup_active_job:')) {
          const parsed = JSON.parse(sessionStorage.getItem(k) || '{}');
          if (typeof parsed?.targetCount === 'number') return parsed.targetCount;
        }
      }
    } catch { /* sessionStorage unavailable (private mode etc.) */ }
    return 0;
  });
  const [activeJobStartedAt, setActiveJobStartedAt] = useState<number>(() => {
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('brand_setup_active_job:')) {
          const parsed = JSON.parse(sessionStorage.getItem(k) || '{}');
          if (typeof parsed?.startedAt === 'number') return parsed.startedAt;
        }
      }
    } catch { /* ignore */ }
    return 0;
  });
  // Full plan size (20 by default). Drives the "virtual locked" placeholder
  // tiles drawn beneath the rendering skeletons + real stubs, so the user
  // sees the FULL 20-tile structure the moment they land on /gallery —
  // BEFORE any backend stub inserts complete (~7s). Real stub DB rows
  // replace these placeholders 1:1 as they arrive via polling. Lazy-init
  // from sessionStorage for the same reason as activeJobTarget.
  const [activeJobTotal, setActiveJobTotal] = useState<number>(() => {
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('brand_setup_active_job:')) {
          const parsed = JSON.parse(sessionStorage.getItem(k) || '{}');
          if (typeof parsed?.totalCount === 'number') return parsed.totalCount;
        }
      }
    } catch { /* ignore */ }
    return 0;
  });

  // Active Ad Strategist batch — parallel to the brand-setup machinery
  // above. Populated by the AS wizard's handoff to /gallery (handleBuildMyAds
  // stashes { jobId, immediatePostIds, stubPostIds, totalCount, startedAt }).
  //
  // Why two separate active-job slots: they can coexist (user kicks off a
  // brand-setup batch on brand A, then jumps to brand B + runs Ad Strategist).
  // Reading both at once lets the partition logic place EACH batch's tiles
  // in their correct slot without crossover.
  const [activeAsJob, setActiveAsJob] = useState<{
    jobId: string;
    immediatePostIds: string[];
    stubPostIds: string[];
    startedAt: number;
  } | null>(() => {
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith('ad_strategist_active_job:')) {
          const parsed = JSON.parse(sessionStorage.getItem(k) || '{}');
          if (parsed?.jobId && Array.isArray(parsed?.immediatePostIds)) {
            return {
              jobId: String(parsed.jobId),
              immediatePostIds: parsed.immediatePostIds.map(String),
              stubPostIds: Array.isArray(parsed.stubPostIds)
                ? parsed.stubPostIds.map(String) : [],
              startedAt: typeof parsed.startedAt === 'number' ? parsed.startedAt : Date.now(),
            };
          }
        }
      }
    } catch { /* ignore */ }
    return null;
  });
  // Set of all post ids belonging to the active AS batch — covers both
  // immediately-rendering tiles + locked stubs. Used by the partition
  // logic to route AS tiles into the active section instead of the
  // older buckets where sticky-bucket would scatter them.
  const activeAsPostIdSet = useMemo<Set<string>>(() => {
    if (!activeAsJob) return new Set();
    return new Set<string>([
      ...activeAsJob.immediatePostIds,
      ...activeAsJob.stubPostIds,
    ]);
  }, [activeAsJob]);

  // Map post.id → signed image URL. Built lazily after listPosts resolves.
  const [imageUrls, setImageUrls] = useState<Record<string, string>>(() => (cacheHit ? galleryCache!.imageUrls : {}));
  // Tracks the image_path we last signed a URL for, per post id. Lets a re-fetch
  // re-sign a post whose image_path CHANGED (e.g. an edited/re-baked creative,
  // same id + new path) instead of skipping it as "already have a URL" — which
  // left stale thumbnails after edits. Empty on mount → first fetch re-signs all
  // (deterministic public URLs, so unchanged posts get identical strings = no
  // <img> reload), which also refreshes anything stale in the hydrated cache.
  const signedPathsRef = useRef<Record<string, string>>({});

  // ── Section collapse state (Spike — bento + animation prototype) ─────
  // Tracks which sections the user has collapsed into a hero card. Default
  // empty (= everything expanded). Per-brand localStorage so a user's
  // preferences survive refresh + brand-switching.
  const collapseStorageKey = `gallery_section_collapsed_v1:${activeBrand?.id ?? 'none'}`;
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    if (typeof window === 'undefined' || !activeBrand?.id) return new Set();
    try {
      const raw = window.localStorage.getItem(collapseStorageKey);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? new Set(arr.map(String)) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    // Reset when active brand changes (the storage key changed too).
    if (typeof window === 'undefined' || !activeBrand?.id) return;
    try {
      const raw = window.localStorage.getItem(collapseStorageKey);
      const arr = raw ? (JSON.parse(raw) as unknown[]) : [];
      setCollapsedSections(new Set(Array.isArray(arr) ? arr.map(String) : []));
    } catch { setCollapsedSections(new Set()); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrand?.id]);
  const toggleSectionCollapse = useCallback((key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        if (typeof window !== 'undefined' && activeBrand?.id) {
          window.localStorage.setItem(collapseStorageKey, JSON.stringify(Array.from(next)));
        }
      } catch { /* private mode / quota */ }
      return next;
    });
  }, [activeBrand?.id, collapseStorageKey]);
  // Map post.id → scheduled tiles for that post (for the status pill).
  const [scheduledByPost, setScheduledByPost] = useState<Record<string, ScheduledPost[]>>(() => (cacheHit ? galleryCache!.scheduledByPost : {}));
  // Schedule modal — opened from a post card's "Schedule" button. If
  // the post already has an upcoming scheduled cohort (one timestamp +
  // N platforms), we open the modal in EDIT mode on those tiles so the
  // user can toggle platforms, change time, or cancel. Empty array =
  // create mode. Stored as an array so the modal can manage multi-
  // platform schedules as a group (per the June fan-out semantics).
  const [scheduling, setScheduling] = useState<
    { post: Post; editingTiles: ScheduledPost[] } | null
  >(null);
  // Start NOT loading when we have a cached gallery for this brand — paint it
  // instantly + refetch in the background (no full-page takeover on return).
  const [loading, setLoading] = useState(() => !cacheHit);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  // Jun 22 — clock-tick state for the stuck-render detector. Bumped
  // every 30s so tiles in RENDERING state re-evaluate isStuck even
  // when no other state change happens (e.g. brand-setup poll loop
  // already ended but 5 stubs got silently stuck).
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  // ── "Generate 20 more" top-up (Jun 18). Strip state machine:
  //   idle  → CTA only
  //   picking → product-picker modal open
  //   running → collapsed status bar, polling renders X/20
  //   done  → strip expands, shows the generated images
  // Loader copy is computed below (topUpBuildMsg), driven off topUpMsgIdx
  // (elapsed seconds). Two visible phases while a batch runs:
  //   BUILDING (topUpRenderedCount === 0): /top-up-20 returns a job id in ~50ms,
  //     but the backend then runs _build_ad_brief → the LONG _build_20_concepts
  //     pass → recipe-pick + stub insert, with NO tiles yet. We PROGRESS through
  //     those real stages, spread across the estimated build time (~6s/ad), and
  //     hold on the last stage if it overruns — instead of one static line.
  //   RENDERING (topUpRenderedCount > 0): tiles are landing → the count IS the
  //     status, so the header shows just "Rendering…" + N/total, no flavor copy.
  const [topUpPickerOpen, setTopUpPickerOpen] = useState(false);
  const [topUpSelectedIds, setTopUpSelectedIds] = useState<string[]>([]);
  // How many ads to generate: 5 / 10 / 20 (default 20). 1 credit each, so the
  // count IS the credit cost. Replaces the old fixed TOP_UP_COST = 20.
  const [topUpCount, setTopUpCount] = useState<5 | 10 | 20>(20);
  // Jun 26 — deep-link entry from /create's "Generate 20 More" card.
  // Reads `location.state.openTopUp` once on mount, opens the existing
  // picker modal, then clears the state so a back/forward nav doesn't
  // re-trigger it. One-shot.
  useEffect(() => {
    const state = location.state as { openTopUp?: boolean } | null;
    if (state?.openTopUp) {
      setTopUpPickerOpen(true);
      // Clear router state so refresh / back-nav doesn't re-fire.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Jun 22 — aspect-ratio choice for the top-up batch. Defaults to 1:1
  // (most common social/IG format). Backend already accepts this on
  // /api/ad-strategist/top-up-20; the UI just needed a picker.
  const [topUpAspectRatio, setTopUpAspectRatio] = useState<string>('1:1');
  // Top-up lifecycle from the module controller (survives nav-away mid-flight).
  // status: idle → prep (POST building copy+templates) → rendering → done.
  const topUpCtl = useSyncExternalStore(subscribeTopUp, getTopUpController);
  const topUpSubmitting = topUpCtl.status === 'prep';
  const topUpJobId = topUpCtl.jobId;
  const topUpTotal = topUpCtl.total;
  const [topUpRenders, setTopUpRenders] = useState<AdConceptRenderRow[]>([]);
  // Rendered posts for this job that lost their (idea_idx, concept_idx) slot
  // link (older/other worker code) — they're still completed renders for this
  // source_job_id, so they MUST count toward progress or the bar stays at 0.
  const [topUpOrphaned, setTopUpOrphaned] = useState(0);
  // Rotating status-message index (ticks every ~2.6s while preparing/rendering)
  // so the user sees WHAT is happening, not one generic line the whole time.
  const [topUpMsgIdx, setTopUpMsgIdx] = useState(0);
  const [topUpExpanded, setTopUpExpanded] = useState(() => topUpController.status === 'done');
  // Tracks WHICH locked tiles are mid-unlock (Jun 17 — Set, was single
  // id). Backend is now async: each unlock POST reserves 1 credit and
  // returns immediately; the actual render fires as an asyncio task.
  // Multiple unlocks can be in flight at once — user clicks N tiles,
  // each shows its own progress spinner, and /gallery polling sees the
  // image_path land for each as their worker completes.
  const [unlockingIds, setUnlockingIds] = useState<Set<string>>(() => new Set());
  // Per-tile start time for the % loader (legacy — the sync-queue path that
  // populated this was removed Aug 2; single unlock now shows GENERATING via
  // the DB-derived isRendering, no per-tile %. Kept read-only so the tile
  // loader's optional %-branch still compiles).
  const [unlockStartTimes] = useState<Map<string, number>>(() => new Map());
  // Bulk-unlock confirm modal state (Jun 17). Holds the batch we're
  // about to fan out + count of locked stubs. null = modal closed.
  const [bulkUnlockTarget, setBulkUnlockTarget] = useState<{
    jobId: string;
    lockedCount: number;
  } | null>(null);
  const [bulkUnlocking, setBulkUnlocking] = useState(false);
  // Tick state — bumped every 500ms while ANY render is in flight so
  // the percentage actually advances. Avoids per-tile setInterval (one
  // shared timer drives all loaders).
  const [progressTick, setProgressTick] = useState(0);
  useEffect(() => {
    const hasInflight =
      unlockingIds.size > 0 ||
      (activeJobTarget > 0 && activeJobStartedAt > 0);
    if (!hasInflight) return;
    const t = setInterval(() => setProgressTick((n) => n + 1), 500);
    return () => clearInterval(t);
  // We only depend on whether there IS an in-flight render — the tick
  // value itself isn't a dep (it's the OUTPUT of the timer).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unlockingIds, activeJobTarget, activeJobStartedAt]);

  // Exponential approach to 100% — caps at 92% so we never claim
  // "done" until the actual render lands. Same curve shape as the
  // wizard's StepPreview progress bar (`100 × (1 − exp(−t/τ))`).
  // Approved-recipe renders at quality=low take ~20-25s (gpt-image-2
  // generate + Pillow crop + GCS upload + DB UPDATE). τ picked so the
  // bar hits ~80% at 25s — visually "almost there" without lying.
  // `progressTick` is unused inside but its inclusion in the closure
  // forces React to recompute when the tick changes.
  const computeProgress = (startedAtMs: number): number => {
    void progressTick; // re-render trigger
    if (!startedAtMs) return 0;
    const elapsedSec = Math.max(0, (Date.now() - startedAtMs) / 1000);
    const tau = 15; // ~80% at 25s, ~92% at 40s
    const pct = 100 * (1 - Math.exp(-elapsedSec / tau));
    return Math.min(92, Math.max(0, pct));
  };
  // Facebook connection state — drives whether the "Post to Facebook"
  // tile button is enabled. Hoisted into the FacebookConnectionProvider
  // (June 8) so navigating away + back doesn't re-fetch — the provider
  // mounts once at app root and stays warm across route changes.
  const { status: fbStatus, refresh: refreshFbStatus } = useFacebookConnection();
  // The post the social caption modal is currently open for (null = closed).
  // Combined post+channel since user can open FB modal OR IG modal from
  // the same row of hover buttons. Modal handles its own in-flight +
  // error state internally; parent fires toast + refetches on success.
  const [socialModal, setSocialModal] = useState<
    { post: Post; channel: SocialChannel } | null
  >(null);
  // In-flight video publish jobs (Jun 23). When the PostToSocialModal
  // submits a video, it calls onVideoJobsSubmitted with the job_ids; we
  // store them here and poll getPublishJob every 5s. Each job goes:
  // processing → publishing → posted (terminal) | failed (terminal). On
  // terminal status we drop the entry + re-fetch posts so the gallery
  // pill updates. Survives in sessionStorage so a refresh keeps polling.
  const [publishJobs, setPublishJobs] = useState<Array<{
    jobId: string;
    channel: SocialChannel;
    postId: string;
  }>>(() => {
    try {
      const raw = sessionStorage.getItem('publish_jobs_inflight');
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  // Persist publishJobs so a navigate away + back keeps polling.
  useEffect(() => {
    try {
      sessionStorage.setItem('publish_jobs_inflight', JSON.stringify(publishJobs));
    } catch {
      // sessionStorage full / disabled — non-fatal.
    }
  }, [publishJobs]);
  // Poll loop lives AFTER fetchPosts is declared (see ~line 945 below) —
  // referencing fetchPosts here would TDZ. Search "Poll loop —" for the
  // effect body.
  // Full-res preview modal — click a tile to open. State holds the post
  // being previewed + a signed full-resolution URL (no transform option, so
  // the user sees original PNG, not the 500px thumb).
  const [previewPost, setPreviewPost] = useState<Post | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // On mobile a tile opens this view-only modal instead of the editor route
  // (editing is desktop-only). This flag shows the "use the desktop version
  // to edit" note in the modal so the user knows why they're not in the editor.
  const [previewEditNotice, setPreviewEditNotice] = useState(false);

  // Masonry column count — viewport-driven, mirrors the legacy CSS Grid
  // breakpoints (was grid-cols-2..8 via Tailwind classes). We render tiles
  // into N flex columns with round-robin distribution so each column packs
  // tightly downward — a square tile next to a 4:5 tile no longer leaves
  // empty space below the shorter one (CSS Grid forced row-aligned tops,
  // which produced the staircase). Native `grid-template-rows: masonry`
  // isn't safe to ship in 2026 (Safari 26 only); a small JS-driven flex-
  // column layout is the portable answer.
  const computeColumnCount = (): number => {
    if (typeof window === 'undefined') return 4;
    const w = window.innerWidth;
    if (w >= 3000) return 8;
    if (w >= 2400) return 7;
    if (w >= 1920) return 6;
    if (w >= 1536) return 5;        // 2xl
    if (w >= 1024) return 4;        // lg/xl
    if (w >= 768) return 3;         // md
    return 2;                       // sm + default
  };
  const [columnCount, setColumnCount] = useState<number>(computeColumnCount);
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = computeColumnCount();
        setColumnCount((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  // fetchPosts(initial): show loading skeleton on first mount only.
  // fetchPosts(background): silent re-fetch during polling — no loading
  // state toggle so the page doesn't flicker every 4s while a brand-
  // setup job is rendering 20 tiles in the background.
  const fetchPosts = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      const [data, sched] = await Promise.all([
        listPosts(activeBrand?.id ?? null),
        fetchScheduledByPost().catch(() => ({})),
      ]);
      setPosts((prev) => {
        // "Load older" tail preservation — once the user has paged past
        // the first 200, MERGE the fresh newest-200 head into the
        // accumulation instead of replacing, so a poll/focus-refetch
        // updates new renders (unlock fills, new batches) without wiping
        // the older tiles the user loaded. Dedupe by id (fresh wins).
        if (olderLoadedRef.current && prev.length > data.length) {
          const freshIds = new Set(data.map((p) => p.id));
          return [...data, ...prev.filter((p) => !freshIds.has(p.id))];
        }
        // Skip the state update entirely if the post list is unchanged
        // — but check more than just ids. Three signals besides id:
        //   1. published_to.length grows when a post is published to
        //      FB/IG (drives the channel chips on the tile).
        //   2. unlocked flips false→true when a stub render completes
        //      (drives the locked-tile → real-image transition).
        //   3. image_path goes from "" to a real path on the same event.
        // Without these, polled re-fetches with the same set of post
        // ids would short-circuit and the UI would stay stale.
        if (
          prev.length === data.length &&
          prev.every((p, i) =>
            p.id === data[i].id &&
            (p.published_to?.length ?? 0) === (data[i].published_to?.length ?? 0) &&
            p.unlocked === data[i].unlocked &&
            (p.image_path || '') === (data[i].image_path || '')
          )
        ) {
          return prev;
        }
        return data;
      });
      // A full 200-length head means older creatives exist beyond it.
      // Don't touch hasMore once older pages are loaded — loadOlder owns
      // it then (else a poll's full head would resurrect the button after
      // the user reached the end).
      if (!olderLoadedRef.current) setHasMore(data.length >= 200);
      setScheduledByPost(sched);

      // Sign URLs in ONE round-trip via getSignedImageUrls (now a
      // synchronous local GCS public-URL builder — post-Phase-1, no
      // network round-trip). Re-sign only posts whose image_path CHANGED
      // since we last signed it (new posts, unlock fills, AND edited creatives
      // that keep their id but get a new path) — so unchanged posts don't
      // churn URL maps + trigger spurious <img> reloads, but edits refresh.
      setImageUrls((prevUrls) => {
        const missing = data.filter((p) => signedPathsRef.current[p.id] !== p.image_path);
        if (missing.length === 0) return prevUrls;
        const newImagePaths = missing
          .filter((p) => !p.image_path.endsWith('.zip'))
          .map((p) => p.image_path);
        const newZipPaths = missing
          .filter((p) => p.image_path.endsWith('.zip'))
          .map((p) => p.image_path);
        // getSignedImageUrls returns a Promise (legacy signature) but
        // resolves synchronously — the GCS bucket is public, the URL is
        // just `https://storage.googleapis.com/{bucket}/{path}`. We can
        // still treat it as fire-and-forget here since the result is
        // applied in the .then() below.
        Promise.all([
          newImagePaths.length > 0
            ? getSignedImageUrls(newImagePaths, 3600, { width: 500, quality: 80 })
            : Promise.resolve({} as Record<string, string>),
          newZipPaths.length > 0
            ? getSignedImageUrls(newZipPaths, 3600)
            : Promise.resolve({} as Record<string, string>),
        ]).then(([imgUrlByPath, zipUrlByPath]) => {
          setImageUrls((latest) => {
            const next = { ...latest };
            for (const p of missing) {
              next[p.id] = imgUrlByPath[p.image_path] || zipUrlByPath[p.image_path] || '';
              signedPathsRef.current[p.id] = p.image_path;
            }
            return next;
          });
        }).catch((err) => {
          console.error('Bulk sign failed', err);
        });
        return prevUrls;
      });
    } catch (err) {
      console.error('Gallery load failed:', err);
      if (!background) setToast({ message: 'Failed to load gallery', type: 'error' });
    } finally {
      if (!background) setLoading(false);
    }
  }, [activeBrand?.id]);

  // "Load older creatives" — fetch the next 200 older than the oldest
  // currently-loaded post (keyset cursor) and APPEND them. The gallery's
  // date sections re-bucket them automatically (Yesterday / per-day / etc.).
  const handleLoadOlder = useCallback(async () => {
    if (loadingOlder) return;
    // Oldest loaded post = sorts LAST in created_at DESC, id ASC: the
    // smallest created_at, or the largest id on a created_at tie.
    let oldest: Post | null = null;
    for (const p of posts) {
      if (!p.created_at) continue;
      if (
        !oldest ||
        p.created_at < oldest.created_at! ||
        (p.created_at === oldest.created_at && p.id > oldest.id)
      ) {
        oldest = p;
      }
    }
    if (!oldest || !oldest.created_at) return;
    setLoadingOlder(true);
    try {
      const older = await listPosts(activeBrand?.id ?? null, {
        beforeCreatedAt: oldest.created_at,
        beforeId: oldest.id,
      });
      olderLoadedRef.current = true;
      if (older.length > 0) {
        setPosts((prev) => {
          const have = new Set(prev.map((p) => p.id));
          const add = older.filter((p) => !have.has(p.id));
          return add.length ? [...prev, ...add] : prev;
        });
        // Sign the newly-loaded batch's image URLs (public GCS URLs) so
        // their tiles render — same pattern as fetchPosts.
        const missing = older.filter(
          (p) => p.image_path && signedPathsRef.current[p.id] !== p.image_path,
        );
        if (missing.length > 0) {
          const imgPaths = missing
            .filter((p) => !p.image_path.endsWith('.zip'))
            .map((p) => p.image_path);
          const zipPaths = missing
            .filter((p) => p.image_path.endsWith('.zip'))
            .map((p) => p.image_path);
          Promise.all([
            imgPaths.length
              ? getSignedImageUrls(imgPaths, 3600, { width: 500, quality: 80 })
              : Promise.resolve({} as Record<string, string>),
            zipPaths.length
              ? getSignedImageUrls(zipPaths, 3600)
              : Promise.resolve({} as Record<string, string>),
          ])
            .then(([imgByPath, zipByPath]) => {
              setImageUrls((latest) => {
                const next = { ...latest };
                for (const p of missing) {
                  next[p.id] =
                    imgByPath[p.image_path] || zipByPath[p.image_path] || '';
                  signedPathsRef.current[p.id] = p.image_path;
                }
                return next;
              });
            })
            .catch((err) => console.error('Older sign failed', err));
        }
      }
      setHasMore(older.length >= 200);
    } catch (err) {
      console.error('Load older failed:', err);
      setToast({ message: 'Failed to load older creatives', type: 'error' });
    } finally {
      setLoadingOlder(false);
    }
  }, [posts, activeBrand?.id, loadingOlder]);

  // First fetch: full-page loader ONLY when there's no cached gallery for this
  // brand. With a cache, refetch silently in the background so the existing
  // tiles stay on screen (no blank "Loading…" takeover on nav-away-and-back).
  useEffect(() => {
    const haveCache = !!galleryCache && galleryCache.brandId === (activeBrand?.id ?? null);
    void fetchPosts(haveCache);
  }, [fetchPosts, activeBrand?.id]);

  // Mirror the live gallery into the module cache so a later remount paints it
  // instantly. Brand-scoped. imageUrls update asynchronously, so this captures
  // them as they resolve too.
  useEffect(() => {
    galleryCache = {
      brandId: activeBrand?.id ?? null,
      posts,
      imageUrls,
      scheduledByPost,
    };
  }, [posts, imageUrls, scheduledByPost, activeBrand?.id]);

  // Refetch when the user navigates back to /gallery from another route,
  // OR when the tab becomes visible again (was hidden by tab-switch or
  // window-blur). Without this, mid-render tiles stay stale until manual
  // refresh — bug surfaced Jun 26 when user navigated away to /creatives
  // and came back to a still-empty grid even though backend had finished
  // 14 renders. Uses background=true so no loading flicker.
  //
  // Lightweight: passive listeners + skip when the page is already visible
  // (focus fires constantly during normal interaction). The double-fire
  // when both events fire simultaneously is fine — fetchPosts internally
  // dedupes via the activeBrand?.id useCallback dep.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === 'visible') void fetchPosts(true); };
    const onVisibility = () => { if (document.visibilityState === 'visible') void fetchPosts(true); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchPosts]);

  // Render-poll (Aug 2) — DB-derived, so it survives navigating to the editor
  // and back (the recurring "Unlock 15 reverts" bug). Any PAID stub still
  // rendering = unlocked (paid) + no image yet + belongs to a batch. This
  // covers brand-setup bulk-unlock AND Generate-20 top-up. Polls fetchPosts
  // every 4s until every such stub has an image, then stops on its own.
  const hasRenderingPaidStubs = posts.some(
    (p) => p.unlocked && !p.image_path && (p.source_setup_job_id || p.source_job_id),
  );
  useEffect(() => {
    if (!hasRenderingPaidStubs) return;
    const t = setInterval(() => { void fetchPosts(true); }, 4000);
    return () => clearInterval(t);
  }, [hasRenderingPaidStubs, fetchPosts]);

  // Poll loop — every 5s, ask the backend for each tracked video publish
  // job's status. On terminal (posted / failed) we drop the entry, refresh
  // posts (so the new published_to pill renders), and show a toast for the
  // failed case (success is implied by the pill appearing). Lives AFTER
  // fetchPosts is declared above — referencing it earlier would TDZ + crash
  // the entire page (caught by the App's error boundary as a generic
  // "Something went wrong"). See sibling state declaration at the top.
  useEffect(() => {
    if (publishJobs.length === 0) return;
    const t = setInterval(async () => {
      const stillInflight: typeof publishJobs = [];
      let anyTerminal = false;
      for (const j of publishJobs) {
        try {
          const r = await getPublishJob(j.jobId);
          if (r.status === 'posted' || r.status === 'failed') {
            anyTerminal = true;
            if (r.status === 'failed') {
              const label = j.channel === 'instagram' ? 'Instagram' : 'Facebook';
              setToast({
                message:
                  `${label} publish failed: ${r.error || 'unknown error'}. ` +
                  `Try again from My Creatives.`,
                type: 'error',
              });
            }
          } else {
            stillInflight.push(j);
          }
        } catch {
          // Transient network failure — keep the job in the queue and
          // try again on the next tick rather than dropping it.
          stillInflight.push(j);
        }
      }
      if (anyTerminal) {
        setPublishJobs(stillInflight);
        void fetchPosts(true);
      }
    }, 5000);
    return () => clearInterval(t);
  }, [publishJobs, fetchPosts]);

  // ── "Generate 20 more" top-up ─────────────────────────────────────────
  // Count BOTH slot-linked rendered posts AND orphaned ones (rendered for this
  // job but missing the idx link) — the endpoint queries by source_job_id, so
  // their sum is the true "how many of the 20 are done." Without orphaned, the
  // bar sat at 0 the whole time. Clamp to the total for display safety.
  const topUpTotalEff = topUpTotal || topUpCount;
  // Fallback count from already-fetched posts so a remount mid-render shows
  // real progress instantly — before the first renders-poll repopulates
  // topUpRenders (which resets to [] on unmount).
  const topUpFromPosts = topUpJobId
    ? posts.filter((p) => p.source_job_id === topUpJobId && !!p.image_path && !!imageUrls[p.id]).length
    : 0;
  // SINGLE SOURCE OF TRUTH (Jun 30): the header counts ONLY the revealed tiles
  // (gallery posts with image_path + a signed url) — the exact set that draws
  // the thumbnails. The old `max(endpointCount, postsCount)` mixed two unsynced
  // sources (live /renders poll vs the cached posts list) and ran ahead of what
  // was actually shown (e.g. header 11 while 6 tiles revealed). Now header =
  // section = thumbnails, always.
  const topUpRenderedCount = Math.min(topUpTotalEff, topUpFromPosts);
  const topUpDone = topUpCtl.status === 'done';

  // BUILDING copy (shown only while topUpRenderedCount === 0 — no tiles yet).
  // topUpMsgIdx = elapsed seconds (ticks 1/s below). Spread the real backend
  // stages evenly across the estimated build time (~6s/ad: 5→~30s, 20→~2min) so
  // the line ADVANCES through the stages and holds on the last if it overruns —
  // instead of stalling on one static message the whole time.
  const topUpBuildCount = topUpTotalEff || topUpCount;
  const TOP_UP_BUILD_MSGS = [
    'Studying your brand & selected products…',
    `Writing ${topUpBuildCount} distinct ad concepts…`,
    'Designing distinct creative directions…',
    'Matching a scene & template to each…',
    'Queuing your ads to render…',
  ];
  const topUpBuildEstSec = Math.max(20, topUpBuildCount * 6);
  const topUpBuildMsg =
    TOP_UP_BUILD_MSGS[
      Math.min(
        TOP_UP_BUILD_MSGS.length - 1,
        Math.floor((topUpMsgIdx / topUpBuildEstSec) * TOP_UP_BUILD_MSGS.length),
      )
    ];

  const handleTopUpGenerate = useCallback(async () => {
    if (topUpSelectedIds.length === 0) {
      setToast({ message: 'Pick at least one product first.', type: 'error' });
      return;
    }
    // Close the picker + reset progress NOW so the status strip takes over
    // immediately and reads as one continuous flow: preparing → rendering →
    // done. (Was: picker stayed up with a spinner, then the strip appeared
    // only once rendering started — so the "writing copy / picking" phase was
    // invisible.)
    setTopUpPickerOpen(false);
    setTopUpRenders([]);
    setTopUpOrphaned(0);
    setTopUpMsgIdx(0);
    setTopUpExpanded(false);
    // Lifecycle in the module controller so navigating away mid-flight keeps it
    // alive — the POST resolves into the controller even after this component
    // unmounts, and the remounted gallery picks the job back up.
    setTopUpController({ status: 'prep', jobId: null, total: topUpCount, startedAt: Date.now() });
    try {
      const res = await adStrategistTopUp20({
        productIds: topUpSelectedIds,
        aspectRatio: topUpAspectRatio,
        count: topUpCount,
      });
      setTopUpController({ status: 'rendering', jobId: res.job_id, total: res.total || topUpCount });
      void creditsRefresh();
    } catch (err) {
      setTopUpController({ ...topUpIdle });
      if (err instanceof InsufficientCreditsError) {
        setToast({
          message: `You need ${topUpCount} credits — opening upgrade…`,
          type: 'error',
        });
        navigate('/checkout');
      } else {
        setToast({
          message: err instanceof Error ? err.message : 'Could not start generation.',
          type: 'error',
        });
      }
    }
  }, [topUpSelectedIds, topUpAspectRatio, topUpCount, creditsRefresh, navigate]);

  const handleTopUpDismiss = useCallback(() => {
    setTopUpController({ ...topUpIdle });
    setTopUpRenders([]);
    setTopUpOrphaned(0);
    setTopUpMsgIdx(0);
    setTopUpExpanded(false);
  }, []);

  // Cancel an in-flight / stuck batch. Best-effort backend cleanup of the
  // unrendered stubs (read the LIVE module jobId to avoid a stale closure),
  // then clear the strip — controller → idle also wipes the sessionStorage
  // key, so a wedged "0/20" (e.g. the worker died on a backend restart)
  // unsticks immediately.
  const handleTopUpCancel = useCallback(() => {
    const jid = topUpController.jobId;
    if (jid) void cancelAdStrategistTopUp(jid);
    setTopUpController({ ...topUpIdle });
    setTopUpRenders([]);
    setTopUpOrphaned(0);
    setTopUpMsgIdx(0);
    setTopUpExpanded(false);
    void fetchPosts(true);
  }, [fetchPosts]);

  // Poll the top-up job's renders every 4s while in flight. Also refreshes
  // the gallery (background) so the 20 also flow into the date sections as
  // they land. Stops + auto-expands once all 20 are rendered.
  useEffect(() => {
    // Only poll during the RENDER phase. On a 'done'-state remount the jobId is
    // still set, so without this guard the effect would re-arm the poll.
    if (topUpCtl.status !== 'rendering' || !topUpJobId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    // Batch-start anchor for the absolute backstop. startedAt is set at 'prep'
    // and persists across remounts (setTopUpController merges + sessionStorage).
    const startedAt = topUpCtl.startedAt;
    const ABSOLUTE_CAP_MS = 6 * 60 * 1000;
    let finalizingStale = false; // call the 6-min backstop endpoint at most once

    const finish = async () => {
      // Flipping status→'done' re-runs THIS effect and tears the poll down
      // (status is a dep), so a final await fetchPosts pulls + signs every
      // rendered post BEFORE we stop polling — else the last stragglers'
      // tiles/count would freeze below the true total (the "stuck at 16" bug).
      await fetchPosts(true);
      if (cancelled) return;
      setTopUpController({ status: 'done' });
      setTopUpExpanded(true);
      void creditsRefresh();
      if (timer) { clearInterval(timer); timer = null; }
    };

    const poll = async () => {
      try {
        const res = await getAdStrategistJobRenders(topUpJobId);
        if (cancelled) return;
        setTopUpRenders(res.renders);
        setTopUpOrphaned(res.orphaned_count || 0);
        void fetchPosts(true);
        const rendered =
          res.renders.filter((r) => r.status === 'rendered').length +
          (res.orphaned_count || 0);
        const failed = res.failed_count || 0;
        // COMPLETE only when every slot reached a TERMINAL state — rendered OR
        // backend-confirmed-failed (render_error). No blind timer, so a slow-
        // but-fine tail is never false-flagged as failed (the Jul-10 bug), and
        // a genuinely stuck slot (150s timeout → render_error) still finalizes
        // the batch as "N done · M failed" the moment the backend confirms it.
        if (topUpTotal > 0 && rendered + failed >= topUpTotal) {
          await finish();
          return;
        }
        // 6-min ABSOLUTE backstop: slots still pending with NO render_error =
        // the worker likely died mid-render (Cloud Run scale-to-0 / task GC),
        // leaving the credit reserved. Ask the backend to refund those leaked
        // reservations + stamp render_error, then complete. Guard → once.
        if (
          !finalizingStale &&
          startedAt > 0 &&
          Date.now() - startedAt > ABSOLUTE_CAP_MS
        ) {
          finalizingStale = true;
          await finalizeStaleTopUp(topUpJobId);
          if (cancelled) return;
          await finish();
        }
      } catch {
        // transient — keep polling
      }
    };
    void poll();
    timer = setInterval(poll, 4000);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [topUpCtl.status, topUpJobId, topUpTotal, fetchPosts, creditsRefresh]);

  // Rotate the status message every ~2.6s while the strip is active (preparing
  // = POST in flight; rendering = job polling, not yet done) so the user sees a
  // changing, specific description instead of one static line.
  useEffect(() => {
    const active = topUpSubmitting || (!!topUpJobId && !topUpDone);
    if (!active) return;
    const id = setInterval(() => setTopUpMsgIdx((i) => i + 1), 1000);
    return () => clearInterval(id);
  }, [topUpSubmitting, topUpJobId, topUpDone]);

  // Ad Strategist Build-my-ads handoff. The wizard writes
  // `ad_strategist_active_job:{brandId}` to sessionStorage with
  // {jobId, immediatePostIds[], stubPostIds[], totalCount, startedAt}.
  // Only the immediate ids are tracked here — the stub ids stay locked
  // until the user clicks Unlock on each one. We poll fetchPosts every
  // 4s until every immediate id reports unlocked=true + image_path, or
  // until 5min as a safety stop. No backend status endpoint — the source
  // of truth is the posts themselves. Independent from the brand-setup
  // poll below; both can run simultaneously.
  useEffect(() => {
    if (!activeAsJob) return;
    const trackedIds = new Set(activeAsJob.immediatePostIds);
    if (trackedIds.size === 0) return;
    const startedAt = activeAsJob.startedAt;
    const MAX_POLL_MS = 5 * 60 * 1000;  // 5 min safety stop
    const brandKey = activeBrand?.id || 'unknown';
    const storageKey = `ad_strategist_active_job:${brandKey}`;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_POLL_MS) {
        try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
        if (!cancelled) setActiveAsJob(null);
        return;
      }
      await fetchPosts(true);
      if (cancelled) return;
      try {
        const fresh = await listPosts(activeBrand?.id ?? null);
        const stillLocked = fresh.filter(
          (p) => trackedIds.has(p.id) && (!p.unlocked || !p.image_path),
        );
        if (stillLocked.length === 0) {
          // All immediate renders landed — clear the active-job marker
          // so the bucketing logic releases its grip on those tiles +
          // any future page refresh starts clean.
          try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
          if (!cancelled) setActiveAsJob(null);
          return;
        }
      } catch { /* ignore one bad poll */ }
      timer = setTimeout(tick, 4000);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [fetchPosts, activeAsJob, activeBrand?.id]);

  // Pick up an in-flight brand-setup job handed off from the wizard via
  // sessionStorage. Poll status every 8s (bumped from 4s — the worker
  // takes ~30s per render so polling 4x per render-cycle was wasteful).
  // Only refetch the posts list when completedCount actually advanced
  // (not on every poll); the prior implementation hit the backend 2x
  // per poll regardless of whether anything changed, drowning out
  // other user requests (My Brand, My Products) during the render
  // burst.
  useEffect(() => {
    // sessionStorage key is brand-scoped so a render job from Brand A
    // doesn't leak skeleton tiles onto Brand B's gallery. The wizard
    // writes `brand_setup_active_job:${brandId}` on Generate; we read
    // only the entry for the currently-active brand.
    const brandId = activeBrand?.id;
    if (!brandId) return;
    const storageKey = `brand_setup_active_job:${brandId}`;
    let raw: string | null = null;
    try { raw = sessionStorage.getItem(storageKey); } catch { /* ignore */ }
    if (!raw) {
      // Reset job state — covers brand-switch case where the previous
      // brand had an active job but the new one doesn't.
      setActiveJob(null);
      setActiveJobTarget(0);
      setActiveJobTotal(0);
      setActiveJobStartedAt(0);
      return;
    }
    let parsed: {
      jobId: string; targetCount: number; totalCount?: number; startedAt?: number;
    } | null = null;
    try { parsed = JSON.parse(raw); } catch { /* corrupt */ }
    if (!parsed?.jobId) return;
    setActiveJobTarget(parsed.targetCount);
    setActiveJobTotal(parsed.totalCount ?? parsed.targetCount);
    setActiveJobStartedAt(parsed.startedAt ?? Date.now());

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let lastCompletedCount = -1;

    const poll = async () => {
      try {
        const s = await getBrandSetupStatus(parsed!.jobId);
        if (cancelled) return;
        setActiveJob(s);
        // Sync activeJobTotal from /status response in case sessionStorage
        // didn't carry it (e.g. cold-load after manual refresh while
        // worker is still running).
        if (typeof s.totalCount === 'number' && s.totalCount > 0) {
          setActiveJobTotal(s.totalCount);
        }
        // ALWAYS refetch posts while the job is in-flight. The old
        // "only refetch when completed_count advances" optimization saved
        // ~5 GETs per batch but caused the stub inserts (~7s after /start)
        // to stay invisible until the first render landed ~30s later — the
        // "loading time same, just in a different location" symptom. With
        // at most ~15 polls per batch the extra traffic is negligible.
        // We still track lastCompletedCount so terminal logic below
        // continues to work as before.
        const terminalCount = s.completedCount + s.failedCount;
        if (terminalCount > lastCompletedCount) {
          lastCompletedCount = terminalCount;
          // A render just completed → a credit was spent server-side. Refresh
          // the credits context so the sidebar balance tracks the spend live
          // (previously it stayed stale at the pre-batch number until a manual
          // page refresh — the "shows 20, should be 15" bug).
          try { creditsRefresh(); } catch { /* non-fatal */ }
        }
        void fetchPosts(true);  // background mode — no loading flicker
        // Detect worker death: status says 'running' but no progress
        // for 3+ minutes. The backend self-heals to 'failed' at the 5
        // min mark; we pre-empt at 3 to stop spinning skeletons faster.
        // No-op if last_progress_at is missing (older job rows / first
        // few seconds before the worker writes anything).
        let staleAsTerminal = false;
        if (s.status === 'running' && s.lastProgressAt) {
          const last = new Date(s.lastProgressAt).getTime();
          if (Number.isFinite(last) && Date.now() - last > 3 * 60 * 1000) {
            staleAsTerminal = true;
            console.warn(
              '[gallery] job stale: no progress for >3min, treating as failed',
              { jobId: parsed!.jobId, lastProgressAt: s.lastProgressAt },
            );
          }
        }
        if (
          s.status === 'completed' || s.status === 'failed' ||
          s.status === 'cancelled' || staleAsTerminal
        ) {
          try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
          // If the batch STALLED (worker died mid-render), refund the leaked
          // in-flight reservations FIRST — via each stub's persisted
          // credit_tx_id — then report the REAL refunded count. This banner
          // previously claimed "remaining credits were refunded" when nothing
          // was actually refunded (self-heal only marked the job failed); now
          // the claim is true and the balance visibly bounces back. A genuine
          // failure (the worker already refunded inline) just surfaces s.error.
          const stalled =
            staleAsTerminal ||
            (s.status === 'failed' && /no progress|worker died/i.test(s.error || ''));
          if (stalled) {
            let refunded = 0;
            try {
              refunded = (await finalizeBrandSetupStale(parsed!.jobId)).refunded;
            } catch { /* best-effort — the #13 sweeper is the backstop */ }
            void creditsRefresh();
            setJobErrorBanner(
              'Brand setup stopped responding — the worker crashed mid-batch. ' +
              'Any finished images are saved' +
              (refunded > 0
                ? `, and ${refunded} credit${refunded === 1 ? '' : 's'} for the unfinished ` +
                  `render${refunded === 1 ? '' : 's'} ${refunded === 1 ? 'was' : 'were'} refunded.`
                : '.'),
            );
          } else if (s.error) {
            setJobErrorBanner(s.error);
          }
          // Clear the LIVE job pointer so the "rendering…" banner
          // disappears, but KEEP activeJobTarget + activeJobStartedAt
          // populated. Two reasons:
          //   1. effectiveDone now equals thisJobRenders.length, so
          //      pendingSkeletons naturally computes to 0 — the
          //      skeletons disappear without the partition collapsing.
          //   2. If we zeroed activeJobStartedAt here, isFromActiveJob()
          //      would return false for every post → all 5 renders fall
          //      into `olderPosts` → backend's DESC order applies → the
          //      entire batch flips L↔R the moment slot 5 lands. Keeping
          //      the partition stable means the freshly-completed batch
          //      stays in ASC order until the user navigates away.
          //   3. sessionStorage was already cleared above, so the next
          //      page mount lazy-inits to 0 (fresh visit → no partition,
          //      standard gallery DESC order).
          // Final credit sync on terminal — the batch is done spending.
          try { creditsRefresh(); } catch { /* non-fatal */ }
          setActiveJob(null);
          // One final refetch to catch any tail-edge writes the worker
          // committed after the status flip.
          void fetchPosts(true);
          return;
        }
        timer = setTimeout(poll, 8000);
      } catch (err) {
        console.error('[gallery] job poll failed', err);
        timer = setTimeout(poll, 12000);
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // fetchPosts intentionally omitted — closure reads latest version.
    // Re-fires when activeBrand changes so brand-switch picks up the new
    // brand's job (if any) and stops polling the old brand's job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrand?.id]);

  // Skeleton tile count for the in-flight brand-setup job.
  //
  // We can't rely on activeJob.completedCount alone — two ways it goes
  // stale: (a) the worker dies mid-job (Cloud Run scale-to-0, uvicorn
  // restart in dev) and the count freezes at its last write, while posts
  // may already be in the DB; (b) brief race between the post insert and
  // the job-row UPDATE, so a poll lands with N posts but N-1 in the
  // counter. We also need to render skeletons BEFORE the first poll
  // completes (covered by activeJobTarget>0 short-circuit, which is
  // lazy-init from sessionStorage so it's set on first paint).
  //
  // Defence: count posts whose created_at is at or after the job's
  // startedAt (with a small clock-skew buffer) — those are this job's
  // output — and take the MAX of that vs. the reported counter as the
  // effective "done" count. Worst case both agree; best case we recover
  // from stale counters without showing phantom skeletons.
  // Partition posts so we can render in the order the user expects:
  //   1. Real renders from THIS brand-setup job (top-left, OLDEST first —
  //      so the first one to finish stays anchored at position 0 forever
  //      and each new completion lands in the NEXT empty skeleton slot
  //      without shoving its siblings sideways).
  //   2. Skeletons for the in-flight renders (fill the remaining target slots)
  //   3. Stubs from THIS job — locked tiles waiting for unlock (15 of them)
  //   4. Older posts — previous brand setups, wizard saves, etc.
  // The user sees: renders + skeletons fill the TOP-LEFT (left-to-right as
  // each render lands, no reflow), 15 locked tiles fill the row(s) after,
  // older content sits below all of that.
  const isFromActiveJob = (p: Post): boolean => {
    // Ad Strategist batch — explicit post-id membership. AS inserts all
    // 20 stubs synchronously and stashes the ids in sessionStorage, so
    // we don't need a created_at heuristic for this path.
    if (activeAsPostIdSet.size > 0 && activeAsPostIdSet.has(p.id)) return true;
    // Brand-setup batch — created_at >= job startedAt (2s clock skew).
    if (activeJobStartedAt <= 0) return false;
    // Missing created_at → treat as "now" (a fresh batch member), NOT epoch 0.
    // An undated batch tile with epoch 0 fell out of the active window → landed
    // in a phantom "December 1969" section (Aug 1 defense; backend Fix A is the
    // real cure, this guards any other undated path).
    const t = p.created_at ? new Date(p.created_at).getTime() : Date.now();
    // Only ACTUAL brand-setup posts join this section. Top-up ("Generate 20
    // more") posts carry source_job_id (NOT source_setup_job_id); without this
    // guard the bare created_at window swept them in → the 35-card merge. A
    // brand-setup post always has source_setup_job_id set. (Jul 23)
    return t >= activeJobStartedAt - 2000 && !!p.source_setup_job_id;
  };
  // Stable-slot ordering for the active brand-setup batch.
  //
  // Background: a stub row (no image_path) and its rendered form (image_path
  // filled in by the worker) are the SAME post.id. Earlier versions split
  // them into separate `thisJobRenders` (renders only) and `thisJobStubs`
  // (no-image only) groups, then concatenated those two groups for display.
  // That meant when a stub transitioned to a render, its tile JUMPED from
  // the back of the array (stub section) to the front (render section) —
  // exactly the "tile flies across the grid" bug.
  //
  // Fix: order all activeJob posts by created_at ASC (matches the slot
  // insertion order — stubs are inserted with monotonic timestamps), and
  // treat the FIRST `activeJobTarget` as "actively rendering" (skeleton OR
  // render, same slot position either way). The remaining stubs stay
  // locked. A stub→render transition just flips the tile's display state
  // in place; it does NOT change array position.
  // ── Sticky-position assignment ────────────────────────────────────────
  // For every post visible in this brand: assign a stable position number
  // the first time we see it. Once assigned, the position NEVER changes.
  // Section displays sort by position ASC → tiles stay where the user
  // first saw them, period.
  //
  // First-time assignment order:
  //   • Active AS batch — immediate ids in their backend order, then stub
  //     ids in their backend order. This makes the 5 renders cluster at
  //     positions 0-4 of the section (top-left of the masonry layout).
  //   • Everything else — posts[] order. The backend now sorts by
  //     (created_at DESC, id ASC), so this is deterministic across polls.
  //
  // Pruning: positions are deleted for posts that disappear from the
  // visible list (delete, brand switch). Keeps localStorage from
  // growing unbounded.
  {
    let changed = false;
    const visibleIds = new Set(posts.map((p) => p.id));
    let nextPos = 0;
    postPositionRef.current.forEach((v) => { if (v >= nextPos) nextPos = v + 1; });

    // Pass 1: active AS batch ordering. Walk immediate ids then stub ids
    // in their persisted order — gives the 5 renders the lowest positions
    // (= leftmost columns of their section).
    if (activeAsJob) {
      const inOrder: string[] = [
        ...activeAsJob.immediatePostIds,
        ...activeAsJob.stubPostIds,
      ];
      for (const id of inOrder) {
        if (!postPositionRef.current.has(id) && visibleIds.has(id)) {
          postPositionRef.current.set(id, nextPos++);
          changed = true;
        }
      }
    }

    // Pass 1.5: VERSION INHERITANCE (Jun 17). When the user saves an
    // edit, a new posts row is INSERTed with parent_post_id pointing
    // to the prior head. The prior head gets is_head=false (so it
    // drops out of /gallery's list — we never see it again). If we
    // assigned the new row a fresh position via Pass 2, it would land
    // at nextPos (rightmost). Instead: inherit the parent's slot —
    // take its position, free the parent's entry from the ref. Net:
    // the same column stays occupied by the same creative chain
    // across edits, just with newer pixels.
    for (const p of posts) {
      if (postPositionRef.current.has(p.id)) continue;
      const parentId = p.parent_post_id;
      if (!parentId) continue;
      const parentPos = postPositionRef.current.get(parentId);
      if (parentPos == null) continue;
      // Parent will no longer be in /gallery (is_head=false). Hand the
      // position to the child + delete the parent's stale entry.
      postPositionRef.current.set(p.id, parentPos);
      postPositionRef.current.delete(parentId);
      changed = true;
    }

    // Pass 2: every other unassigned post, with brand-setup batch posts
    // (those carrying a `slot_idx`) sorted by slot_idx FIRST so their
    // sticky positions land in slot order (0→col 0, 1→col 1, etc.).
    // This is what guarantees the tile-stays-put invariant for brand
    // setup: parallel INSERTs are non-deterministic in created_at + id
    // tiebreak, but slot_idx is the truth. Posts without slot_idx fall
    // through in posts[] arrival order (no change for non-batch posts).
    const slotted = posts
      .filter((p) => !postPositionRef.current.has(p.id) && p.slot_idx != null)
      .slice()
      .sort((a, b) => (a.slot_idx as number) - (b.slot_idx as number));
    const unslotted = posts.filter(
      (p) => !postPositionRef.current.has(p.id) && p.slot_idx == null,
    );
    for (const p of slotted) {
      postPositionRef.current.set(p.id, nextPos++);
      changed = true;
    }
    for (const p of unslotted) {
      postPositionRef.current.set(p.id, nextPos++);
      changed = true;
    }

    // Prune disappearing ids — only when we actually have posts (avoid
    // wiping the map on a transient fetch failure that returned []).
    if (posts.length > 0) {
      for (const id of Array.from(postPositionRef.current.keys())) {
        if (!visibleIds.has(id)) {
          postPositionRef.current.delete(id);
          changed = true;
        }
      }
    }
    if (changed) persistPositions();
  }

  // Compare by position. Falls back to id when both are unassigned (should
  // never happen — Pass 2 above assigns everything — but defensive).
  const compareByPosition = (a: Post, b: Post): number => {
    const ap = postPositionRef.current.get(a.id);
    const bp = postPositionRef.current.get(b.id);
    if (ap !== undefined && bp !== undefined) return ap - bp;
    if (ap !== undefined) return -1;
    if (bp !== undefined) return 1;
    return a.id < b.id ? -1 : 1;
  };

  // Active batch order. Pass 1 above puts active AS immediate ids first
  // (positions 0-4) then stub ids (positions 5-19) — that batch ordering
  // is deterministic.
  //
  // BUT for brand-setup batches Pass 2 falls back to posts[] arrival
  // order, which assigns positions 0-19 to 20 stubs that all landed at
  // the same instant (parallel asyncio.gather). The 5 slots that will
  // ACTUALLY render are spread randomly across positions 0-19, so when
  // renders UPDATE the stubs in-place they end up scattered — often at
  // the bottom (see Jun 15 gotcha: top 5 stayed "RENDERING" forever
  // while real tiles landed at positions 13-17).
  //
  // Fix: sort rendered tiles BEFORE locked stubs within the active
  // batch, then by sticky position within each group. As renders land
  // (image_path goes NULL → set), the tile bubbles to the top
  // automatically. The skeleton "RENDERING" placeholder may show
  // different stub posts during the render phase (whichever happen to
  // have the lowest sticky position), but those are all visually
  // identical — what matters is that the COMPLETED renders land at
  // the top, not the bottom.
  // Sort by sticky position ONLY — id determines slot which determines
  // column, period. A skeleton at column 7 renders in place at column 7;
  // no tile ever moves when a render lands. Jun 17 — restored after the
  // "rendered tiles cluster at top" + render-order experiments both
  // produced visible movement that the user rejected.
  const activeAll = posts
    .filter(isFromActiveJob)
    .slice()
    .sort(compareByPosition);

  // For AS batches: the "actively rendering" count is exactly the number
  // of immediate post ids (5 in the default 5+15 split). For brand-setup,
  // it falls back to activeJobTarget which is read from sessionStorage.
  const effectiveActiveJobTarget = activeAsJob
    ? activeAsJob.immediatePostIds.length
    : activeJobTarget;

  // First N tiles are the "actively rendering" set — each is either a
  // skeleton-state stub or its rendered form. The rest (positions N..end)
  // are the "still-locked" stubs (15 of 20 in the default 20/5 batch).
  const activelyRendering = effectiveActiveJobTarget > 0
    ? activeAll.slice(0, effectiveActiveJobTarget)
    : [];
  const stillLockedStubs = effectiveActiveJobTarget > 0
    ? activeAll.slice(effectiveActiveJobTarget)
    : activeAll.filter((p) => !p.image_path);
  // For pendingSkeletons calculation: actively-rendering tiles whose
  // image_path hasn't landed yet are still "in flight".
  const thisJobRenders = activelyRendering.filter((p) => !!p.image_path);

  // All non-active posts, sorted: rendered FIRST then locked stubs,
  // each sub-group ordered by sticky position. Jun 15 — applies the
  // same rendered-first rule the active job uses, because parallel
  // INSERTs at brand-setup time gave the 5 to-be-rendered stubs random
  // positions across 0-19. Without this sort, renders that complete
  // AFTER the active-job sessionStorage went stale (user navigated
  // away + back) would scatter through their section instead of
  // landing at the top. The "stable position" invariant still holds
  // WITHIN each rendered/locked sub-group.
  // Same rule for non-active sections: pure sticky position. An Unlock
  // on a stub flips its image_path in place — no movement.
  const olderPostsAll = posts
    .filter((p) => !isFromActiveJob(p))
    .slice()
    .sort(compareByPosition);

  // Jul 14 — the /creatives prev/next nav order is written AFTER olderSections
  // is built (below), because it must match the SECTIONED on-screen order, not
  // the flat compareByPosition list. Writing it here (pre-sections) made the
  // arrows jump across date buckets / batches. See the write after olderSections.

  // Group older posts into sections by source_job_id (Ad Strategist) or
  // source_setup_job_id (brand-setup batch). Posts without either land in
  // a single "Loose creatives" bucket at the end. Within a section, rendered
  // posts come before locked stubs (mirrors the old flat-grid ordering so
  // the user's existing work sits at the top of each section).
  // Jun 22 — collapsed 'as' + 'setup' tones into 'loose'. AS/Brand-setup
  // labels are no longer surfaced (per user direction); batches just show
  // as time-stamped runs within the current period.
  type SectionTone = 'loose';
  interface GallerySectionData {
    key: string;
    tone: SectionTone;
    title: string;
    /** Newest post timestamp in the section — drives the section subtitle
     *  ("Generated Jun 8") and the inter-section sort order. */
    newestAt: number;
    /** Higher = nearer the top. Date buckets get descending order values
     *  so Today sits above Yesterday above the older buckets. */
    order: number;
    posts: Post[];
    /** Bulk-unlock CTA (Jun 17): set when a current-period section
     *  groups a brand-setup batch that has locked stubs. */
    bulkUnlock?: { jobId: string; lockedCount: number } | null;
    /** True when at least one post in the section is still rendering
     *  (no image_path). Drives the icon/subtitle "rendering" cue. */
    stillRendering?: boolean;
  }
  // Date-organized sections (Jun 18): latest first. Today + Yesterday get
  // their own section; each of the next few days is its own dated section;
  // then "Last 30 days"; then grouped by month, newest month first. Source
  // (Ad Strategist / Brand kickoff / loose) no longer drives grouping — the
  // active in-flight brand-setup batch is still its own live top section,
  // handled separately above.
  //
  // Bucket boundaries are intentionally simple to tweak: change the day
  // thresholds (1 / 6 / 29) below to adjust how long a day stays its own
  // section before collapsing into "Last 30 days" / month buckets.
  const olderSections = (() => {
    const MONTHS = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    const dayMs = 86400000;
    const now = new Date();
    const todayMid = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
    ).getTime();

    // Jun 22 — today subdivides into 5 time-of-day buckets. Order field
    // is descending-by-recency within today so the latest bucket sits on
    // top. Early morning (00:00 - 04:59) sits ABOVE Tonight (21:00 - 23:59)
    // because at e.g. 12:11 AM the wee-hours post is the most recent
    // event of today, not the prior evening's "Tonight".
    //   early-morning: 00:00 - 04:59  (12:47 AM reads as early morning, not late night)
    //   tonight:       21:00 - 23:59
    //   evening:       17:00 - 20:59
    //   afternoon:     12:00 - 16:59
    //   morning:       05:00 - 11:59
    function todayTimeBucket(hour: number): { id: string; label: string; order: number } {
      if (hour < 5) return { id: 'early-morning', label: 'Early morning', order: 5 };
      if (hour >= 21) return { id: 'tonight', label: 'Tonight', order: 4 };
      if (hour >= 17) return { id: 'evening', label: 'Evening', order: 3 };
      if (hour >= 12) return { id: 'afternoon', label: 'Afternoon', order: 2 };
      return { id: 'morning', label: 'Morning', order: 1 };
    }

    function bucketFor(ts: number): { key: string; title: string; order: number } {
      const d = new Date(ts);
      const dMid = new Date(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
      ).getTime();
      const diffDays = Math.round((todayMid - dMid) / dayMs);
      if (diffDays <= 0) {
        // Today — subdivide by time-of-day. Order encodes recency within
        // today (Tonight = highest) so date sections sort newest first.
        const tb = todayTimeBucket(d.getHours());
        return {
          key: `d:today:${tb.id}`,
          title: `Today · ${tb.label}`,
          order: 1_000_000 + tb.order,  // 1_000_001..1_000_004
        };
      }
      if (diffDays === 1)
        return { key: 'd:yesterday', title: 'Yesterday', order: 999_999 };
      if (diffDays <= 6) {
        // Each recent day its own section, newest nearest the top.
        const title = d.toLocaleDateString(undefined, {
          weekday: 'long',
          month: 'short',
          day: 'numeric',
        });
        return { key: `day:${dMid}`, title, order: 999_900 - diffDays };
      }
      if (diffDays <= 29)
        return { key: 'd:last30', title: 'Last 30 days', order: 900_000 };
      // Older → group by calendar month, newest month first. year*12+month
      // is monotonic + always below the Last-30 bucket.
      const order = d.getFullYear() * 12 + d.getMonth();
      return {
        key: `m:${d.getFullYear()}-${d.getMonth()}`,
        title: `${MONTHS[d.getMonth()]} ${d.getFullYear()}`,
        order,
      };
    }

    // Jun 22 — dropped asGroups + setupGroups entirely. All non-active
    // posts flow through date buckets. Within the CURRENT time-of-day
    // bucket on today, each batch (source_job_id / source_setup_job_id)
    // gets its OWN section with a HH:MM timestamp so users see their
    // recent runs distinctly. PAST time-of-day buckets (Morning, etc.)
    // collapse all batches into one section per period. Older dates
    // collapse into a single section per date.
    const sections: GallerySectionData[] = [];
    const nowBucketKey = bucketFor(Date.now()).key;  // e.g. 'd:today:afternoon'

    // currentRunGroups: keyed by source_job_id || source_setup_job_id ||
    // 'loose' — one entry per batch in the CURRENT time-of-day bucket.
    // pastBuckets: keyed by bucket key (e.g. 'd:today:morning' or
    // 'd:yesterday') — collapses all batches in that bucket to one
    // section.
    const currentRunGroups = new Map<string, Post[]>();
    const pastBuckets = new Map<string, GallerySectionData>();

    for (const p of olderPostsAll) {
      // Missing created_at → bucket as "now" (Today), never epoch 0. An
      // undated post at t=0 created a phantom "December 1969" section (Aug 1
      // defense; backend Fix A stops the unlock path from emitting empty dates).
      const t = p.created_at ? new Date(p.created_at).getTime() : Date.now();
      const b = bucketFor(t);
      if (b.key === nowBucketKey) {
        // Current period — subdivide by run (or 'loose' for non-batch posts).
        const runKey =
          p.source_job_id
            ? `j:${p.source_job_id}`
            : p.source_setup_job_id
              ? `s:${p.source_setup_job_id}`
              : 'loose';
        const arr = currentRunGroups.get(runKey);
        if (arr) arr.push(p);
        else currentRunGroups.set(runKey, [p]);
      } else {
        // Past period — collapse to one section per bucket.
        const existing = pastBuckets.get(b.key);
        if (existing) {
          existing.posts.push(p);
          if (t > existing.newestAt) existing.newestAt = t;
        } else {
          // Strip the "Today · " prefix on past-period titles (it's
          // implicit when we're past it) so they read "Morning" /
          // "Afternoon" / etc. Yesterday + older keep their full title.
          const title = b.key.startsWith('d:today:')
            ? b.title.replace(/^Today · /, '')
            : b.title;
          pastBuckets.set(b.key, {
            key: b.key,
            tone: 'loose',
            title,
            newestAt: t,
            order: b.order,
            posts: [p],
          });
        }
      }
    }

    // Current period batches → one section per run. Order encoded as
    // newestAt epoch-seconds + a high base (1_500_000) so they sort
    // ABOVE past today periods (which use bucketFor's 1_000_001+ range).
    for (const [runKey, gposts] of currentRunGroups) {
      const isLoose = runKey === 'loose';
      // Within a BATCH (runKey 'j:<id>' or 's:<id>'): keep slot_idx ASC
      // so tile positions stay stable (Issue #5 fix — Jun 22). Tiles
      // don't bubble to top when render completes; they flip skeleton →
      // image in place.
      //
      // LOOSE (manually generated singles/carousels — Jun 22): sort
      // created_at DESC so the newest creative appears LEFTMOST.
      // Carousels save ~90s later than singles you started after them
      // (4-frame sequential render), so chronological-ASC pushed them
      // to the end of the list even when clicked first. DESC matches
      // photo-app expectations (most recent first) and keeps freshly-
      // made carousels visible without trailing the row.
      const ordered = gposts.slice().sort((a, b) => {
        if (isLoose) {
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          if (ta !== tb) return tb - ta;  // DESC — newest first
          return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
        }
        // Batch path — slot_idx ASC + created_at ASC + id ASC.
        const sa = a.slot_idx ?? 1e9;
        const sb = b.slot_idx ?? 1e9;
        if (sa !== sb) return sa - sb;
        const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
        const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (ta !== tb) return ta - tb;
        return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
      });
      const newestAt = ordered.reduce((mx, p) => {
        const t = p.created_at ? new Date(p.created_at).getTime() : 0;
        return t > mx ? t : mx;
      }, 0);
      const stillRendering = ordered.some((p) => !p.image_path);
      // Bulk-unlock CTA: only set for source_setup_job_id batches
      // with locked stubs (so the user can mass-unlock from the
      // section header).
      let bulkUnlock: { jobId: string; lockedCount: number } | null = null;
      if (runKey.startsWith('s:')) {
        const sid = runKey.slice(2);
        const lockedCount = ordered.filter((p) => !p.image_path).length;
        if (lockedCount > 0) bulkUnlock = { jobId: sid, lockedCount };
      }
      sections.push({
        key: `run:${runKey}`,
        tone: 'loose',
        // Title format: "Today · 1:00 PM" — run timestamp. The
        // user explicitly wanted batch granularity for the
        // current period so two runs in the same afternoon show
        // separately, then collapse next period.
        title: `Today · ${new Date(newestAt).toLocaleTimeString(undefined, {
          hour: 'numeric', minute: '2-digit',
        })}`,
        newestAt,
        // Higher than past-period base so current-period batches sit
        // above past-period sections within today. epoch-seconds gives
        // newer batches a higher order.
        order: 1_500_000 + Math.floor(newestAt / 1000),
        posts: ordered,
        bulkUnlock,
        stillRendering,
      });
    }

    // Past period buckets — sort posts newest first within each.
    for (const s of pastBuckets.values()) {
      // Keep the sticky-position (slot_idx-derived) order so an aged brand-setup
      // batch stays laid out by slot — top-left → bottom-right — exactly as it did
      // while it was the active/current-day section. (Was: created_at newest-first,
      // which scattered the parallel-inserted renders once a batch fell into a
      // past-date bucket — the "rendered in place then flipped" bug.)
      s.posts.sort(compareByPosition);
      // The bulk "Unlock all N" CTA survives into past buckets: if every locked
      // stub here belongs to ONE brand-setup batch, offer it from the header
      // (past buckets used to drop it, so an aged batch lost the button).
      const lockedStubs = s.posts.filter((p) => !p.image_path && p.source_setup_job_id);
      if (lockedStubs.length > 0) {
        const jobIds = new Set(lockedStubs.map((p) => p.source_setup_job_id as string));
        if (jobIds.size === 1) {
          s.bulkUnlock = { jobId: [...jobIds][0], lockedCount: lockedStubs.length };
        }
      }
      sections.push(s);
    }

    // Setup sections (high order) sit above the date buckets; newest first.
    sections.sort((a, b) => b.order - a.order);
    return sections;
  })();

  // Jul 14 — write the visible post-id sequence for the /creatives/:id prev/next
  // arrows in the EXACT on-screen order: active job section first (top of the
  // grid), then each olderSection in render order (sorted by `order` DESC), each
  // section's posts in its own display order. This matches what the user sees, so
  // left/right walk neighbors instead of jumping across date buckets / batches.
  // Stubs (no image_path) excluded — they have no detail page (would 404).
  if (typeof window !== 'undefined') {
    const navOrder = [
      ...activeAll,
      ...olderSections.flatMap((s) => s.posts),
    ]
      .filter((p) => !!p.image_path)
      .map((p) => p.id);
    try {
      sessionStorage.setItem(
        `creatives_nav:${activeBrand?.id ?? 'none'}`,
        JSON.stringify(navOrder),
      );
    } catch { /* quota / private mode — prev/next falls back to no-op */ }
  }

  // Reported-done count from the backend's job row + the real-render
  // count derived from THIS job's posts (defensive — both can lag for
  // different reasons; we take the max).
  const reportedDone = activeJob
    ? activeJob.completedCount + activeJob.failedCount
    : 0;
  // Kept around (exposed via items[] indirectly through activelyRendering)
  // so polling loops can check "are we still waiting on renders?". The
  // raw `pendingSkeletons` count from the older split-list approach is
  // gone — activelyRendering.filter(p => !p.image_path).length gives the
  // same number on demand.
  const effectiveDone = Math.max(reportedDone, thisJobRenders.length);

  // Auto-dismiss toasts: success = 3s, error = 15s.
  // Errors get a longer window because they often have actionable info
  // the user needs to read (e.g. "go to facebook.com → Settings → ...").
  // Also dump error toasts to the console — they used to vanish before
  // the user could screenshot them.
  useEffect(() => {
    if (!toast) return;
    if (toast.type === 'error') {
      console.error('[gallery toast]', toast.message);
    }
    const dwellMs = toast.type === 'error' ? 15000 : 3000;
    const t = setTimeout(() => setToast(null), dwellMs);
    return () => clearTimeout(t);
  }, [toast]);

  const handleConfirmDelete = async (id: string) => {
    const post = posts.find((p) => p.id === id);
    if (!post) return;
    try {
      await deletePost(post.id, post.image_path);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      setImageUrls((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setToast({ message: 'Post deleted', type: 'success' });
    } catch (err) {
      console.error('Delete failed:', err);
      setToast({ message: 'Delete failed', type: 'error' });
    }
    setDeleteId(null);
  };

  // Unlock a locked tile. Async-backed (Jun 17 refactor).
  //   STUB (no image_path): backend reserves 1 credit, spawns the render
  //     as an asyncio task, returns {status: 'rendering', post_id}
  //     immediately. UI shows the % loader on the tile until the next
  //     /gallery poll picks up the new image_path. Multiple stubs can
  //     be in flight at once (no single-click gate).
  //   ALREADY-RENDERED (has image_path, unlocked=false): backend flips
  //     the flag synchronously, returns the full Post. Optimistic flip
  //     for snappier UI.
  // On error: revert + toast. Refreshes credit balance on stub renders
  // (committed in the backend at success).
  // Single-tile Unlock (Aug 2 rewrite — paid-stub model, same as unlock-all).
  // OLD: a localStorage-persisted worker-pool queue fired SYNC ~25s renders and
  // held the "rendering" state in-memory (unlockingIds). Navigating away wiped
  // it AND the persisted queue re-fired the unlock on return → tile re-locked,
  // credit re-charged, same stub re-rendered. NEW: one async call reserves 1
  // credit, marks the stub paid+queued, and renders it via Cloud Task. The tile
  // then shows GENERATING via the DB-derived isRendering (survives navigation);
  // the render-poll pulls the fill (auto-show). Backend is idempotent (a paid
  // stub is a no-op) so a stray re-click can't double-charge. `unlockingIds`
  // gives instant spinner feedback for the ~1s before fetchPosts returns.
  const handleUnlock = useCallback(
    async (postId: string) => {
      if (unlockingIds.has(postId)) return;  // in-flight guard
      setUnlockingIds((prev) => new Set(prev).add(postId));
      try {
        await unlockPost(postId);   // reserve + mark paid+queued + enqueue; fast
        await fetchPosts(true);     // pull queued state → tile flips to isRendering
        try { creditsRefresh(); } catch { /* non-fatal */ }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Unlock failed';
        setToast({ message: msg, type: 'error' });
      } finally {
        setUnlockingIds((prev) => {
          const next = new Set(prev);
          next.delete(postId);
          return next;
        });
      }
    },
    [unlockingIds, fetchPosts, creditsRefresh],
  );

  // Bulk "Unlock all N" — Aug 2 rewrite. THE FIX for the recurring
  // "tiles revert to UNLOCK on navigation" bug. The old code pushed N ids into
  // the in-memory processUnlockQueue and fired N sync unlocks — so the
  // "rendering" state lived in the browser (unlockingIds) and was WIPED when
  // the gallery unmounted (open editor → back), leaving in-flight stubs showing
  // UNLOCK again. Now it hits ONE backend endpoint that reserves the credits,
  // marks all N stubs paid+queued, and renders them as a Cloud Task. The tiles
  // then show GENERATING via the DB-derived isRendering (isStub && unlocked &&
  // isPaidBatchTile), which survives navigation. A background poll (below) pulls
  // the fills until every image lands.
  const handleBulkUnlock = async () => {
    if (!bulkUnlockTarget || bulkUnlocking) return;
    const { jobId } = bulkUnlockTarget;
    setBulkUnlocking(true);
    try {
      await unlockAllBatch(jobId);
      // Pull the now-paid+queued state so the stubs flip to isRendering
      // (GENERATING) immediately; the render-poll effect drives them to images.
      await fetchPosts(true);
      try { creditsRefresh(); } catch { /* non-fatal */ }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not unlock all';
      setToast({ message: msg, type: 'error' });
    } finally {
      setBulkUnlocking(false);
      setBulkUnlockTarget(null);
    }
  };

  // Download via Blob URL so the browser uses our filename + doesn't
  // navigate away from the gallery.
  //
  // The grid's `imageUrls` map points at the TRANSFORMED thumbnail (500px
  // WebP) for fast rendering. Download must serve the ORIGINAL — re-sign
  // the path without the transform option so the user gets full-resolution
  // PNG bytes.
  // Open preview modal — signs a full-res URL (no transform) so the user
  // sees the original PNG, not the 500px thumbnail.
  const handlePreview = async (post: Post) => {
    if (post.image_path.endsWith('.zip')) return; // carousel previews not supported yet
    if (post.image_path.endsWith('.mp4')) return; // video opens on the detail page instead
    setPreviewPost(post);
    setPreviewUrl(null);
    setPreviewLoading(true);
    try {
      const fullUrl = await getSignedImageUrl(post.image_path, 3600);
      setPreviewUrl(fullUrl);
    } catch (err) {
      console.error('Preview failed:', err);
      setToast({ message: 'Could not load image', type: 'error' });
      setPreviewPost(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // A finished tile was activated (click / Enter). On MOBILE (< lg) editing
  // isn't supported, so a single-image tile opens the view-only preview modal
  // (with a "use desktop to edit" note) instead of the /creatives editor
  // route. Carousels (.zip) + videos (.mp4) can only be shown on the detail
  // page, so those still navigate there. On desktop everything opens the
  // editor as before.
  const handleTileOpen = (post: Post) => {
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 1023px)').matches;
    const isSingleImage =
      !post.image_path.endsWith('.zip') && !post.image_path.endsWith('.mp4');
    if (isMobile && isSingleImage) {
      setPreviewEditNotice(true);
      void handlePreview(post);
      return;
    }
    navigate(`/creatives/${post.id}`, { state: { post } });
  };

  // Escape closes the preview modal.
  useEffect(() => {
    if (!previewPost) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPreviewPost(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [previewPost]);

  const handleDownload = async (post: Post) => {
    try {
      const fullUrl = await getSignedImageUrl(post.image_path, 3600);
      const res = await fetch(fullUrl);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const ext = post.image_path.endsWith('.zip')
        ? 'zip'
        : post.image_path.endsWith('.mp4')
          ? 'mp4'
          : 'png';
      link.download = `post-${post.template_id || 'untitled'}-${post.id.slice(0, 8)}.${ext}`;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Download failed:', err);
      setToast({ message: 'Download failed', type: 'error' });
    }
  };

  // Toggle the ★ Starred shortlist flag. Optimistic — flip in-memory first
  // (instant, works on mobile tap), persist, revert on failure.
  const handleToggleStar = async (post: Post) => {
    const next = !post.starred;
    setPosts((prev) =>
      prev.map((p) => (p.id === post.id ? { ...p, starred: next } : p)),
    );
    try {
      await setPostStarred(post.id, next);
    } catch (err) {
      console.error('Star toggle failed:', err);
      // Revert on failure.
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, starred: post.starred } : p)),
      );
      setToast({ message: 'Could not update — try again', type: 'error' });
    }
  };

  // -------- Facebook integration --------
  // fbStatus itself comes from FacebookConnectionProvider — no fetch here.
  // We DO refresh after the OAuth callback bounce so the just-completed
  // connection lands in the cache without a manual page reload, AND show
  // a toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fb = params.get('fb');
    if (fb === 'connected') {
      const page = params.get('page');
      // The callback passes ?ig=<username> when the connected Page has a linked
      // IG Business account (auth_facebook.py auto-finalize). Read it directly
      // so we can confirm Instagram immediately, without waiting on the refresh.
      const igParam = params.get('ig');
      const url = new URL(window.location.href);
      url.searchParams.delete('fb');
      url.searchParams.delete('page');
      url.searchParams.delete('ig');
      window.history.replaceState({}, '', url.toString());
      // Refresh the cached status (this is ALSO what makes the Instagram chip
      // publish-ready at post time), then confirm BOTH platforms in one toast —
      // prefer the ?ig param, fall back to the freshly-fetched status.
      void (async () => {
        const fresh = await refreshFbStatus();
        const ig = igParam || fresh?.instagramUsername;
        const message = ig
          ? `Connected · Facebook${page ? ` (${page})` : ''} · Instagram (@${ig})`
          : `Facebook connected${page ? ` · ${page}` : ''} — no Instagram Business account is linked to this Page`;
        setToast({ message, type: 'success' });
      })();
    } else if (fb === 'error') {
      const msg = params.get('msg') || 'Could not connect Facebook.';
      setToast({ message: msg, type: 'error' });
      const url = new URL(window.location.href);
      url.searchParams.delete('fb');
      url.searchParams.delete('msg');
      window.history.replaceState({}, '', url.toString());
    }
  }, [refreshFbStatus]);

  // Unified open-modal handler. Validates connection prereqs only —
  // carousel + video are both supported in v1. Modal handles the AI-suggest
  // fetch + publish + error display internally. For video, modal calls
  // postVideoToFacebook / postVideoToInstagram (async; returns immediately
  // with a job_id), then the gallery's poll loop tracks status.
  const handlePostToSocial = (post: Post, channel: SocialChannel) => {
    if (socialModal) return;  // a modal is already open
    if (channel === 'facebook' && !fbStatus?.connected) {
      setToast({
        message: 'Connect Facebook first (sidebar → Facebook row)',
        type: 'error',
      });
      return;
    }
    if (channel === 'instagram' && !fbStatus?.instagramUsername) {
      // IG requires (a) FB connected AND (b) FB Page has IG linked.
      // Differentiate the two cases in the toast so the user knows
      // which step to fix.
      const msg = fbStatus?.connected
        ? 'Your Facebook Page doesn’t have an Instagram Business Account linked. Link it on Facebook, then disconnect + reconnect Facebook here.'
        : 'Connect Facebook first — Instagram is discovered via the FB Page.';
      setToast({ message: msg, type: 'error' });
      return;
    }
    setSocialModal({ post, channel });
  };

  // Section controls (Expand-all toggle + saved-count pill). Rendered in
  // TWO places: the desktop header (top-right) and, on mobile, a dedicated
  // row below the "Generate 20 more" promo — so the mobile order reads
  // title+subtitle → promo → controls → creatives (the cramped top-right
  // header cluster was the mobile bug). One node, two mount points.
  const sectionControls = (
    <>
      {/* Group-by-date toggle — sits to the LEFT of Collapse all. ON = per-date
          section cards; OFF = one "All creatives" panel. */}
      {olderSections.length > 0 && (
        <button
          type="button"
          onClick={() => setGroupByDate((v) => !v)}
          aria-pressed={groupByDate}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors cursor-pointer ${groupByDate ? 'bg-primary-light/60 border-primary/40 text-primary' : 'bg-white border-surface-dark/40 text-dark hover:border-primary/40 hover:text-primary'}`}
          title={groupByDate ? 'Grouped by date — tap to show all in one panel' : 'Ungrouped — tap to group by date'}
        >
          <i className="ri-calendar-2-line" />
          <span className="hidden sm:inline">Group by date</span>
        </button>
      )}
      {olderSections.length > 0 && (() => {
        const collapsibleKeys = groupByDate
          ? olderSections.map((s) => s.key)
          : ['all:ungrouped'];
        const allCollapsed = collapsibleKeys.every((k) => collapsedSections.has(k));
        return (
          <button
            type="button"
            onClick={() => {
              setCollapsedSections(() => {
                const next = new Set<string>(
                  allCollapsed ? [] : collapsibleKeys,
                );
                try {
                  if (typeof window !== 'undefined' && activeBrand?.id) {
                    window.localStorage.setItem(
                      collapseStorageKey,
                      JSON.stringify(Array.from(next)),
                    );
                  }
                } catch { /* private mode / quota */ }
                return next;
              });
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-surface-dark/40 text-dark text-xs font-bold hover:border-primary/40 hover:text-primary transition-colors cursor-pointer"
            title={allCollapsed ? 'Expand all sections' : 'Collapse all sections'}
          >
            <i className={allCollapsed ? 'ri-expand-vertical-line' : 'ri-collapse-vertical-line'} />
            <span className="hidden sm:inline">{allCollapsed ? 'Expand all' : 'Collapse all'}</span>
          </button>
        );
      })()}
      <span className="
        inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
        bg-primary/10 border border-primary/30 text-primary
        text-xs font-bold
      ">
        <i className="ri-bookmark-fill" />
        {/* Jul 12 — count RENDERED creatives only (image_path set). Was
            posts.length, which counted the 15 locked brand-setup stubs too,
            so a fresh 5-of-20 batch mislabeled as "20 saved". */}
        {posts.filter((p) => !!p.image_path).length}<span className="hidden sm:inline"> saved</span>
      </span>
    </>
  );

  return (
    <AppLayoutV2>
      {/* Page-local header — title + saved-count pill. Sidebar handles
          all global nav (New Creative / Brand / credits / account) so this
          header doesn't duplicate any of it. */}
      {/* Full-width treatment matches /templates — the saved-creatives
          board IS the value, so we let it use every available pixel
          instead of capping at the standard 1500px content width. */}
      <div className="w-full px-4 lg:px-6 py-8">
        {/* Header treatment matches /templates — icon tile + title +
            subtitle on the left, count chip on the right. Keeps the
            two "browse my stuff" pages visually consistent. */}
        <div className="flex items-start justify-between gap-4 mb-8 lg:mb-10">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <i className="ri-gallery-line text-2xl" />
            </div>
            <div className="flex-1">
              <h1 className="font-display font-bold text-dark text-2xl lg:text-3xl mb-1">
                My Creatives
              </h1>
              <p className="hidden lg:block text-dark-muted text-sm lg:text-base max-w-xl">
                Everything you've saved. Hover a tile to download,
                schedule, or delete.
              </p>
              {/* Jun 26 — active-brand ID chip. DEV-ONLY (import.meta.env.DEV,
                  tree-shaken from prod): it's a debug affordance — first 8 chars
                  shown, click copies the FULL uuid so we can paste it into chat /
                  DB tools for brand-scoped cleanup. Not something end users need. */}
              {import.meta.env.DEV && activeBrand?.id && (
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(activeBrand.id);
                    } catch { /* clipboard unavailable in some contexts */ }
                  }}
                  title={`Click to copy: ${activeBrand.id}`}
                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface border border-surface-dark/40 hover:border-primary/40 hover:bg-primary/5 text-xs font-mono text-dark-muted hover:text-primary transition-colors cursor-pointer"
                >
                  <i className="ri-fingerprint-line text-sm" />
                  <span>brand id: {activeBrand.id.slice(0, 8)}</span>
                  <i className="ri-file-copy-line text-xs opacity-60" />
                </button>
              )}
            </div>
          </div>
          {/* Desktop: controls live top-right in the header. On mobile
              they're hidden here and re-rendered below the promo (see the
              `flex md:hidden` sectionControls row before the sections). */}
          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            {sectionControls}
          </div>
        </div>

        <div>
          {toast && (
            <div
              className={`mb-4 flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                toast.type === 'success'
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              <i className={toast.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} />
              {toast.message}
            </div>
          )}

          {/* Brand-setup job error banner — sticky version of the
              backend's summary. Survives the activeJob=null reset that
              happens when the poll loop sees a terminal status, so the
              user sees "why the batch failed" instead of an empty grid.
              Dismissable via the X. */}
          {jobErrorBanner && (
            <div className="mb-4 flex items-start gap-3 px-4 py-3 rounded-xl bg-red-50 text-red-700 border border-red-200">
              <i className="ri-error-warning-line text-lg flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-bold mb-1">Brand setup didn't finish.</div>
                <div className="leading-relaxed">{jobErrorBanner}</div>
              </div>
              <button
                onClick={() => setJobErrorBanner(null)}
                className="flex-shrink-0 text-red-700/60 hover:text-red-700 transition-colors"
                aria-label="Dismiss"
                title="Dismiss"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
          )}

          {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-10 h-10 rounded-full border-2 border-primary/20 border-t-primary animate-spin mb-4" />
                <p className="text-dark-muted text-sm">Loading your gallery…</p>
              </div>
            ) : posts.length === 0 && activeJobTarget === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 rounded-2xl bg-surface flex items-center justify-center mb-5">
                  <i className="ri-gallery-line text-dark-muted/40 text-3xl" />
                </div>
                <h3 className="font-display font-bold text-dark text-xl mb-2">
                  {activeBrand
                    ? `No creatives for ${activeBrand.name} yet`
                    : 'Nothing here yet'}
                </h3>
                <p className="text-dark-muted text-sm max-w-sm mb-6">
                  {activeBrand
                    ? 'Kick off a new shot to start filling your gallery. Saved creatives will show up here.'
                    : 'Kick off a new shot, or add a brand to generate from. Saved creatives will show up here.'}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button
                    onClick={() => navigate('/create')}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary-hover text-white text-sm font-bold rounded-full transition-all shadow-md shadow-primary/15 cursor-pointer"
                  >
                    <i className="ri-magic-line" />
                    Create New Shot
                  </button>
                  <button
                    onClick={() => navigate('/brands/new')}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-surface text-dark text-sm font-bold rounded-full border border-surface-dark/40 hover:border-primary/40 transition-all cursor-pointer"
                  >
                    <i className="ri-add-circle-line" />
                    {activeBrand ? 'Add Another Brand' : 'Add a Brand'}
                  </button>
                </div>
              </div>
            ) : (
              <LayoutGroup>
              <div>
                {/* "Generate 20 more" top-up strip (Jun 18 — wired to the Ad
                    Strategist engine). Runs CLOSED (a status bar) while the
                    20 ads render, then EXPANDS to reveal them. The renders
                    also flow into the date sections below via source_job_id.
                    State machine: idle CTA → running bar → done reveal. */}
                {(!topUpJobId && !topUpSubmitting) ? (
                  // Premium IDLE state (Jun 22). White card, soft
                  // primary-tinted shadow + ring, gradient icon tile,
                  // tracking eyebrow. No dashed border. Hover lifts the
                  // card 1px and deepens the shadow — same affordance
                  // language the section cards use.
                  <button
                    type="button"
                    onClick={() => {
                      setTopUpSelectedIds([]);
                      setTopUpPickerOpen(true);
                    }}
                    className="w-full mb-5 rounded-2xl bg-white ring-1 ring-primary/15 shadow-lg shadow-primary/10 p-5 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between hover:shadow-xl hover:shadow-primary/20 hover:ring-primary/30 hover:-translate-y-px transition-all cursor-pointer text-left group relative overflow-hidden"
                  >
                    {/* Subtle gradient wash on the right edge so the card
                        has depth without competing with the primary CTA. */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-primary-light/40 via-primary-light/10 to-transparent pointer-events-none"
                    />
                    <div className="flex items-center gap-4 min-w-0 relative z-10">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-accent text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-primary/30 group-hover:scale-105 transition-transform">
                        <i className="ri-sparkling-2-line text-2xl" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-primary leading-none mb-1">
                          Ad Strategist
                        </div>
                        <h2 className="font-display font-bold text-dark text-lg leading-tight">
                          Make AI Magic ✨
                        </h2>
                        <p className="text-xs text-dark-muted leading-tight mt-0.5">
                          Pick products → fresh concepts, rendered in the
                          background. Choose 5, 10 or 20.
                        </p>
                      </div>
                    </div>
                    <span className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold shadow-md shadow-primary/25 group-hover:bg-primary-hover transition-colors relative z-10 w-full sm:w-auto">
                      <i className="ri-arrow-right-line text-base" />
                      Generate Ads
                    </span>
                  </button>
                ) : !topUpDone ? (
                  // Premium RUNNING state — two phases, one surface:
                  //   PREPARING (POST in flight, no job id yet) → rotating
                  //     "writing copy / picking templates" + indeterminate bar.
                  //   RENDERING (job polling) → "N of 20 ready" + a determinate
                  //     bar + rotating render-flavor line. Friendly, reassuring,
                  //     no model names. The bar now actually fills (orphaned
                  //     renders are counted), instead of spinning forever.
                  <div className="w-full mb-5 rounded-2xl bg-white ring-1 ring-primary/25 shadow-lg shadow-primary/15 p-6 relative overflow-hidden">
                    <style>{`
                      @keyframes tu-indet{0%{transform:translateX(-110%)}100%{transform:translateX(360%)}}
                      @keyframes tu-sweep{0%{transform:translateX(-160%) skewX(-14deg)}100%{transform:translateX(280%) skewX(-14deg)}}
                    `}</style>
                    {/* Full-strip takeover: a brand-tinted sheen sweeping across
                        the whole card (clearly "working"), over a soft static
                        wash for depth. */}
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 bg-gradient-to-br from-primary-light/40 via-white to-accent-light/40 pointer-events-none"
                    />
                    <div
                      aria-hidden="true"
                      className="absolute inset-y-0 left-0 w-2/5 bg-gradient-to-r from-transparent via-primary/15 to-transparent pointer-events-none"
                      style={{ animation: 'tu-sweep 2.2s ease-in-out infinite' }}
                    />
                    <div className="relative z-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-6">
                      {/* LEFT — title block */}
                      <div className="min-w-0">
                        <div className="text-[10px] font-bold tracking-[0.16em] uppercase text-primary leading-none mb-1.5">
                          Ad Strategist
                        </div>
                        <h2 className="font-display font-bold text-dark text-xl leading-tight">
                          {topUpRenderedCount > 0 ? 'Generating your ads' : 'Setting up your ads'}
                        </h2>
                        <p className="text-xs text-dark-muted leading-tight mt-1">
                          Keep working — this runs in the background.
                        </p>
                      </div>
                      {/* RIGHT — live counter (left) + Cancel button (far right) */}
                      <div className="flex-shrink-0 flex items-start gap-4 w-full sm:w-auto justify-between sm:justify-start">
                        <div className="text-left sm:text-right">
                          {topUpRenderedCount > 0 ? (
                            // Tiles are landing — the count IS the status; no flavor copy.
                            <>
                              <div className="font-display font-bold text-primary tabular-nums leading-none text-4xl">
                                {topUpRenderedCount}
                                <span className="text-dark-muted/40 text-2xl">/{topUpTotalEff}</span>
                              </div>
                              <div className="text-sm font-semibold text-dark-muted mt-1.5">
                                Rendering…
                              </div>
                            </>
                          ) : (
                            // Building phase (no tiles yet) — progress through the real
                            // backend stages so the user sees what's happening.
                            <div className="text-base font-bold text-primary max-w-[300px]">
                              {topUpBuildMsg}
                            </div>
                          )}
                        </div>
                        {/* Cancel — unsticks a wedged batch (worker died on restart,
                            POST lost, etc.). Cleans up unrendered stubs + clears the
                            strip. */}
                        <button
                          type="button"
                          onClick={handleTopUpCancel}
                          title="Cancel this batch"
                          className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/80 hover:bg-red-500 hover:text-white text-dark-muted text-xs font-bold ring-1 ring-surface-dark/40 hover:ring-red-500 transition-colors backdrop-blur-sm"
                        >
                          <i className="ri-close-line text-sm" />
                          Cancel
                        </button>
                      </div>
                    </div>
                    {/* Full-width progress bar across the whole strip */}
                    <div className="relative z-10 mt-4 h-2 w-full rounded-full bg-surface-dark/25 overflow-hidden">
                      {topUpJobId ? (
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                          style={{
                            width: `${
                              topUpTotal
                                ? Math.round((topUpRenderedCount / topUpTotal) * 100)
                                : 0
                            }%`,
                          }}
                        />
                      ) : (
                        <div
                          className="h-full w-1/3 rounded-full bg-gradient-to-r from-primary to-accent"
                          style={{ animation: 'tu-indet 1.1s ease-in-out infinite' }}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  // Premium DONE state. Same white card, emerald accent
                  // on the icon tile to signal success without losing the
                  // brand-pink family.
                  <div className="w-full mb-5 rounded-2xl bg-white ring-1 ring-emerald-200 shadow-lg shadow-emerald-500/10 p-5 relative overflow-hidden">
                    <div
                      aria-hidden="true"
                      className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-emerald-50 via-emerald-50/40 to-transparent pointer-events-none"
                    />
                    <div className="flex items-center justify-between gap-4 mb-4 relative z-10">
                      <div className="flex items-center gap-4 min-w-0">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white flex items-center justify-center flex-shrink-0 shadow-md shadow-emerald-500/30">
                          <i className="ri-check-line text-2xl" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[10px] font-bold tracking-[0.14em] uppercase text-emerald-700 leading-none mb-1">
                            Ad Strategist
                          </div>
                          <h2 className="font-display font-bold text-dark text-lg leading-tight">
                            {topUpRenderedCount >= topUpTotalEff
                              ? `${topUpTotalEff} new ads ready`
                              : `${topUpRenderedCount} of ${topUpTotalEff} ads ready`}
                          </h2>
                          <p className="text-xs text-dark-muted leading-tight mt-0.5">
                            {topUpRenderedCount < topUpTotalEff
                              ? `${topUpTotalEff - topUpRenderedCount} didn’t finish — hit “Try again” on those tiles below.`
                              : 'Also saved in My Creatives below. Tap any to edit.'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setTopUpExpanded((v) => !v)}
                          className="px-4 py-2 rounded-full text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors cursor-pointer"
                        >
                          {topUpExpanded ? 'Hide' : 'Show'}
                        </button>
                        <button
                          type="button"
                          onClick={handleTopUpDismiss}
                          className="px-4 py-2 rounded-full text-xs font-bold text-dark-muted hover:bg-surface transition-colors cursor-pointer"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                    {topUpExpanded && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2 relative z-10">
                        {/* Source the reveal from THIS job's gallery posts
                            (source_job_id), not the renders poll — so renders
                            that lost their idx slot link ("orphaned") still
                            show. imageUrls is the signed-URL map the tiles use. */}
                        {posts
                          .filter(
                            (p) =>
                              p.source_job_id === topUpJobId &&
                              !!p.image_path &&
                              !!imageUrls[p.id],
                          )
                          .map((p) => (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => navigate(`/creatives/${p.id}`)}
                              className="relative aspect-square rounded-lg overflow-hidden border border-surface-dark/40 hover:border-primary/50 hover:shadow-md transition-all cursor-pointer"
                            >
                              <img
                                src={imageUrls[p.id]}
                                alt=""
                                decoding="async"
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                {/* All / Starred filter (Jul 20) — sits directly below the
                    Generate Ads strip. Starred shows only ★-shortlisted ads. */}
                <div className="flex items-center gap-1 mb-5 p-1 rounded-full bg-surface border border-surface-dark/40 w-fit">
                  <button
                    type="button"
                    onClick={() => setGalleryFilter('all')}
                    aria-pressed={galleryFilter === 'all'}
                    className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors cursor-pointer ${galleryFilter === 'all' ? 'bg-white text-primary shadow-sm' : 'text-dark-muted hover:text-dark'}`}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => setGalleryFilter('starred')}
                    aria-pressed={galleryFilter === 'starred'}
                    className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold transition-colors cursor-pointer ${galleryFilter === 'starred' ? 'bg-white text-primary shadow-sm' : 'text-dark-muted hover:text-dark'}`}
                  >
                    <i className={galleryFilter === 'starred' ? 'ri-star-fill text-primary' : 'ri-star-line'} />
                    Starred
                  </button>
                </div>

                {/* Section controls (group-by-date / collapse-all / saved
                    count) live ONLY in the desktop header top-right now. On
                    mobile they're intentionally hidden to keep the board
                    clean and give the thumbnails full width. */}

                {/* Section-driven layout (June 9). Each batch becomes its
                    own visual card; inside each, tiles use the masonry
                    column pattern. Order:
                      1. This job's REAL renders (top-left, oldest first)
                      2. Skeletons for the still-in-flight renders
                      3. This job's STUBS (locked tiles waiting for unlock)
                      4. OLDER posts (previous batches, saves)
                    items[i] lands in columns[i % columnCount], so reading
                    left-to-right across the top row recovers the source
                    array order. Within a column each tile packs against
                    the previous one — 4:5 tiles in one column don't push
                    siblings in OTHER columns downward, so the staircase
                    gaps below shorter tiles disappear. Bottom is ragged
                    by design (one column ends a tile later than the next). */}
                {(() => {
                  type DisplayItem =
                    | { kind: 'post'; post: Post }
                    | { kind: 'skeleton'; key: string }
                    | { kind: 'virtual-locked'; key: string };
                  // Section-driven layout (June 9). Posts get bucketed by
                  // source_job_id (Ad Strategist) / source_setup_job_id
                  // (brand-setup batch) into separate visual sections so
                  // the user can tell "this batch of 20 came from my AS
                  // run on Jun 8" from "this batch came from onboarding
                  // last week." Section ordering:
                  //   1. ACTIVE — in-flight build-my-ads or brand-setup
                  //      batch (skeletons + stubs + virtual placeholders)
                  //   2. AS / brand-setup batches, newest first
                  //   3. Loose creatives — manual saves / recreate exports
                  // Within each section, the round-robin column layout
                  // packs tiles tightly (same masonry behavior as before).
                  const visibleFromActiveJob =
                    activelyRendering.length + stillLockedStubs.length;
                  const virtualLockedCount = Math.max(
                    0,
                    activeJobTotal - visibleFromActiveJob,
                  );
                  type SectionRender = {
                    key: string;
                    tone: 'active' | 'loose';
                    title: string;
                    subtitle?: string;
                    countLabel: string;
                    icon: string;
                    items: DisplayItem[];
                    // Bulk-unlock support (Jun 17). Set on brand-setup
                    // batches that have locked stubs. null = no CTA.
                    bulkUnlock?: { jobId: string; lockedCount: number } | null;
                    /** True when at least one item is mid-render. Drives
                     *  the section-icon spinner cue (Jun 22). */
                    stillRendering?: boolean;
                    /** Jul 12 — active batch kicked off but NO real stub rows
                     *  have landed in DB yet (~7-20s window). Renders a single
                     *  "Preparing your grid…" loader instead of 20 misleading
                     *  virtual "Unlock" placeholders (the first 5 are already
                     *  auto-rendering; showing Unlock invited a double-charge). */
                    preparing?: boolean;
                  };
                  const renderSections: SectionRender[] = [];
                  if (visibleFromActiveJob > 0 || virtualLockedCount > 0) {
                    const flightCount = activelyRendering.filter((p) => !p.image_path).length;
                    // Bulk unlock in the ACTIVE section (Jun 17 fix). When
                    // the user has just kicked off brand-setup and there
                    // are locked stubs sitting alongside the upfront
                    // renders, surface the same "Unlock all" CTA. Pull
                    // source_setup_job_id off any locked stub (they're
                    // all from the same batch).
                    let activeBulkUnlock: { jobId: string; lockedCount: number } | null = null;
                    if (!activeAsJob) {
                      const lockedActive = stillLockedStubs;
                      const sample = lockedActive[0];
                      const jobId = sample?.source_setup_job_id ?? '';
                      if (lockedActive.length > 0 && jobId) {
                        activeBulkUnlock = {
                          jobId,
                          lockedCount: lockedActive.length,
                        };
                      }
                    }
                    renderSections.push({
                      key: 'active',
                      tone: 'active',
                      title: activeAsJob
                        ? 'Ad Strategist · building'
                        : 'Brand setup · building',
                      subtitle: flightCount > 0
                        ? `${flightCount} render${flightCount === 1 ? '' : 's'} in flight`
                        : undefined,
                      countLabel: activeJobTotal > 0
                        ? `${visibleFromActiveJob}/${activeJobTotal} ads`
                        : `${visibleFromActiveJob} ads`,
                      icon: activeAsJob ? 'ri-radar-line' : 'ri-sparkling-2-line',
                      items: [
                        ...activelyRendering.map<DisplayItem>((p) =>
                          p.image_path
                            ? { kind: 'post', post: p }
                            : { kind: 'skeleton', key: `skel-${p.id}` }
                        ),
                        ...stillLockedStubs.map<DisplayItem>((p) => ({ kind: 'post', post: p })),
                        // Jun 21 — count UP (slot 0, 1, 2 …) so the placeholder
                        // sequence matches the slot order the renders fill in
                        // top→bottom. The old `total-1 - i` form was counting
                        // DOWN, which made the first-rendered real tile land
                        // at the BOTTOM of the active grid (and visually feel
                        // like the batch was building bottom→top). Numbering
                        // them in slot order matches every other "by slot_idx"
                        // ordering invariant in this file (see compareByPosition
                        // + the Pass 2 slot sort above).
                        ...Array.from<DisplayItem>(
                          { length: virtualLockedCount },
                          (_, i): DisplayItem => {
                            // Start AFTER the already-visible tiles' slot
                            // range so virtual-locked keys don't collide with
                            // any real-tile slot. visibleFromActiveJob is the
                            // active section's already-shown count.
                            const startSlot = visibleFromActiveJob;
                            return {
                              kind: 'virtual-locked',
                              key: `vlock-${startSlot + i}`,
                            };
                          },
                        ),
                      ],
                      bulkUnlock: activeBulkUnlock,
                      // No real active-job rows in DB yet → show the grid
                      // loader instead of virtual "Unlock" placeholders.
                      preparing: visibleFromActiveJob === 0 && activeJobTotal > 0,
                    });
                  }
                  if (!groupByDate && olderSections.length > 0) {
                    // Ungrouped: one "All creatives" panel — every saved post,
                    // no date headers.
                    const allPosts = olderSections.flatMap((s) => s.posts);
                    renderSections.push({
                      key: 'all:ungrouped',
                      tone: 'loose',
                      title: 'All creatives',
                      subtitle: `${allPosts.length} creative${allPosts.length === 1 ? '' : 's'}`,
                      countLabel: `${allPosts.length} ad${allPosts.length === 1 ? '' : 's'}`,
                      icon: 'ri-image-line',
                      items: allPosts.map<DisplayItem>((p) => ({ kind: 'post', post: p })),
                      bulkUnlock: null,
                      stillRendering: olderSections.some((s) => s.stillRendering),
                    });
                  } else {
                  for (const sec of olderSections) {
                    const formatDate = (ms: number) => {
                      const d = new Date(ms);
                      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                    };
                    // Jun 22 — HH:MM stamp for the section subtitle. User
                    // direction: every section shows the time the latest
                    // post in it was generated. Today buckets get the
                    // time + nothing else; older sections get the date.
                    const formatTime = (ms: number) => {
                      const d = new Date(ms);
                      return d.toLocaleTimeString(undefined, {
                        hour: 'numeric', minute: '2-digit',
                      });
                    };
                    const isRunSection = sec.key.startsWith('run:');
                    const isTodaySection = sec.key.startsWith('d:today:') || isRunSection;
                    // Subtitle: timestamp of newest post in the section.
                    // Today's per-run sections already encode the time in
                    // the title, so they get a render-state subtitle
                    // ("X generating…"); past-today sections show "Latest
                    // at HH:MM"; older sections show the full date.
                    const subtitle = isRunSection
                      ? (sec.stillRendering
                          ? `${sec.posts.filter((p) => !p.image_path).length} generating…`
                          : `${sec.posts.length} creative${sec.posts.length === 1 ? '' : 's'}`)
                      : isTodaySection
                        ? `Latest at ${formatTime(sec.newestAt)}`
                        : `Latest ${formatDate(sec.newestAt)} · ${formatTime(sec.newestAt)}`;
                    // Icon: morning/afternoon/evening/tonight cues for
                    // past-today buckets; sparkle for individual runs (each
                    // current-period batch); image for older dates.
                    const icon = isRunSection
                      ? 'ri-sparkling-2-line'
                      : isTodaySection && sec.key.endsWith(':morning')
                        ? 'ri-sun-line'
                        : isTodaySection && sec.key.endsWith(':afternoon')
                          ? 'ri-sun-fill'
                          : isTodaySection && sec.key.endsWith(':evening')
                            ? 'ri-sun-cloudy-line'
                            : isTodaySection && sec.key.endsWith(':tonight')
                              ? 'ri-moon-clear-line'
                              : 'ri-image-line';
                    renderSections.push({
                      key: sec.key,
                      tone: sec.tone,
                      title: sec.title,
                      subtitle,
                      countLabel: `${sec.posts.length} ad${sec.posts.length === 1 ? '' : 's'}`,
                      icon,
                      items: sec.posts.map<DisplayItem>((p) => ({ kind: 'post', post: p })),
                      bulkUnlock: sec.bulkUnlock ?? null,
                      stillRendering: sec.stillRendering,
                    });
                  }
                  }

                  // Starred tab (Jul 20): show only starred posts. Drop the
                  // in-flight active section + locked/virtual placeholders,
                  // keep starred post items, drop now-empty sections.
                  const displaySections = galleryFilter === 'starred'
                    ? renderSections
                        .map((s) => ({
                          ...s,
                          items: s.items.filter(
                            (it) => it.kind === 'post' && it.post.starred,
                          ),
                          bulkUnlock: null,
                        }))
                        .filter((s) => s.items.length > 0)
                        .map((s) => ({
                          ...s,
                          countLabel: `${s.items.length} ad${s.items.length === 1 ? '' : 's'}`,
                        }))
                    : renderSections;

                  if (galleryFilter === 'starred' && displaySections.length === 0) {
                    return (
                      <div className="rounded-2xl bg-white ring-1 ring-surface-dark/30 shadow-sm p-10 flex flex-col items-center text-center gap-2">
                        <div className="w-12 h-12 rounded-2xl bg-primary-light text-primary flex items-center justify-center">
                          <i className="ri-star-line text-2xl" />
                        </div>
                        <h3 className="font-display font-bold text-dark text-lg">No starred ads yet</h3>
                        <p className="text-sm text-dark-muted max-w-sm">
                          Tap the <i className="ri-star-line text-primary" /> on any ad to
                          shortlist your keepers — they’ll collect here.
                        </p>
                      </div>
                    );
                  }

                  let runningIdx = 0;
                  return displaySections.map((section) => {
                    const items = section.items;
                    const sectionStartIdx = runningIdx;
                    runningIdx += items.length;
                    // Jun 22 — unified white-card treatment for ALL section
                    // tones. Date sections (loose) used to be flat
                    // bg-surface; now they get the same card chrome as AS
                    // + setup batches for visual consistency.
                    const toneBg =
                      section.tone === 'active' ? 'bg-primary/5 border-primary/30'
                      : 'bg-white border-surface-dark/40';
                    // Different per-tone gradients on the SectionHero inner
                    // backdrop so the cards are still distinguishable at a
                    // glance (Jun 22 — `loose` is the new addition; uses a
                    // neutral surface gradient distinct from AS pink).
                    // Run sections (each current-period batch) get the
                    // primary-tinted icon to flag they're fresh; date
                    // buckets stay neutral.
                    const iconBg =
                      section.tone === 'active' ? 'bg-primary/15 text-primary'
                      : section.key.startsWith('run:') ? 'bg-primary/10 text-primary'
                      : 'bg-surface-dark/40 text-dark-muted';
                    // Spike: the ACTIVE section never collapses — it's
                    // mid-render, the user wants to watch the tiles
                    // populate. Everything else is user-toggleable.
                    const isCollapsible = section.tone !== 'active';
                    const isCollapsed = isCollapsible && collapsedSections.has(section.key);

                    // Pick the first 4 RENDERED thumbnails for the hero
                    // collage (skip skeletons + virtual-locked placeholders
                    // since those have no image to show off).
                    const heroPosts: Post[] = [];
                    for (const it of items) {
                      if (it.kind === 'post' && it.post.image_path
                          && !it.post.image_path.endsWith('.zip')
                          && !it.post.image_path.endsWith('.mp4')) {
                        heroPosts.push(it.post);
                      }
                      if (heroPosts.length >= 4) break;
                    }

                    return (
                      <motion.section
                        key={section.key}
                        layout
                        transition={{ type: 'spring', stiffness: 280, damping: 30 }}
                        className={`rounded-2xl border ${toneBg} ${
                          isCollapsed ? 'p-3' : 'p-5'
                        } mb-5 overflow-hidden`}
                      >
                        <header className={`flex flex-wrap sm:flex-nowrap items-center justify-between gap-3 ${
                          isCollapsed ? 'mb-0' : 'mb-4'
                        }`}>
                          <div
                            className={`flex flex-wrap items-center gap-3 min-w-0 w-full sm:w-auto sm:flex-1 ${
                              isCollapsible ? 'cursor-pointer select-none' : ''
                            }`}
                            onClick={isCollapsible
                              ? () => toggleSectionCollapse(section.key)
                              : undefined}
                            role={isCollapsible ? 'button' : undefined}
                            tabIndex={isCollapsible ? 0 : -1}
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${iconBg} flex-shrink-0`}>
                              <i className={`${section.icon} text-xl`} />
                            </div>
                            <div className="min-w-0">
                              <h2 className="font-display font-bold text-dark text-base leading-tight truncate">
                                {section.title}
                              </h2>
                              {section.subtitle && (
                                <p className="text-xs text-dark-muted leading-tight">
                                  {section.subtitle}
                                </p>
                              )}
                            </div>
                            {/* Bulk-unlock affordance — INSIDE the title
                                cluster (Jun 17) so it sits visually next
                                to the section name instead of floating
                                far right. Bumped to text-sm + px-5/py-2.5
                                so it reads at a glance. stopPropagation on
                                click so the row's collapse handler
                                doesn't fire when the user hits Unlock all. */}
                            {section.bulkUnlock && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (section.bulkUnlock) {
                                    setBulkUnlockTarget(section.bulkUnlock);
                                  }
                                }}
                                className="w-full sm:w-auto justify-center mt-1 sm:mt-0 sm:ml-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold flex items-center gap-2 hover:bg-primary-hover transition-colors flex-shrink-0 shadow-md shadow-primary/25 cursor-pointer"
                              >
                                <i className="ri-flashlight-line text-base" />
                                <span>
                                  Unlock all {section.bulkUnlock.lockedCount}
                                  {' · '}{section.bulkUnlock.lockedCount} cr
                                </span>
                              </button>
                            )}
                          </div>
                          <span className="px-2.5 py-1 rounded-full bg-white border border-surface-dark/30 text-xs font-bold text-dark-muted flex-shrink-0">
                            {section.countLabel}
                          </span>
                          {isCollapsible && (
                            <button
                              type="button"
                              onClick={() => toggleSectionCollapse(section.key)}
                              className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-surface-dark/30 text-dark-muted hover:text-primary hover:border-primary/40 transition-colors cursor-pointer flex items-center justify-center"
                              aria-label={isCollapsed ? 'Expand section' : 'Collapse section'}
                              title={isCollapsed ? 'Expand' : 'Collapse'}
                            >
                              <motion.i
                                className="ri-arrow-down-s-line text-lg"
                                animate={{ rotate: isCollapsed ? 0 : 180 }}
                                transition={{ duration: 0.2 }}
                              />
                            </button>
                          )}
                        </header>
                        <AnimatePresence initial={false} mode="wait">
                          {isCollapsed ? (
                            <motion.div
                              key="hero"
                              initial={{ opacity: 0, scale: 0.96 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.96 }}
                              transition={{ duration: 0.18 }}
                              onClick={() => toggleSectionCollapse(section.key)}
                              className="mt-3 group cursor-pointer"
                            >
                              <SectionHero
                                posts={heroPosts}
                                totalCount={items.length}
                                imageUrls={imageUrls}
                                gradientClass={
                                  // Run sections share the same warm
                                  // gradient regardless of which period
                                  // they're in; past-today buckets get
                                  // per-period gradients (morning/...);
                                  // older dates fall to the neutral one.
                                  section.key.startsWith('run:')
                                    ? HERO_GRADIENTS.as
                                    : section.tone === 'loose' && section.key.startsWith('d:today:')
                                      ? HERO_GRADIENTS[`loose:${section.key.split(':').pop()}`]
                                        ?? HERO_GRADIENTS.loose
                                      : HERO_GRADIENTS[section.tone] ?? HERO_GRADIENTS.loose
                                }
                              />
                            </motion.div>
                          ) : (
                        <motion.div
                          key="grid"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          className="flex gap-3 items-start"
                        >
                          {section.preparing ? (
                            // Jul 12 — grid-level loader for the ~7-20s before
                            // the backend's stub rows land. Replaces the 20
                            // virtual "Unlock" placeholders (misleading — the
                            // first 5 are already rendering in the background).
                            <div className="w-full flex flex-col items-center justify-center gap-3 py-16 text-dark-muted">
                              <i className="ri-loader-4-line text-3xl animate-spin text-primary" />
                              <span className="text-sm font-semibold text-dark">
                                Preparing your grid…
                              </span>
                              <span className="text-xs text-dark-muted/70">
                                Setting up your ads and starting the first renders.
                              </span>
                            </div>
                          ) : (() => {
                  // Distribute round-robin so item k goes to column k % N.
                  // Reading the first row across columns therefore yields
                  // items 0, 1, 2, ..., N-1 in array order — exactly what
                  // the brand-setup batch ordering wants.
                            const columns: { item: DisplayItem; globalIdx: number }[][] =
                              Array.from({ length: columnCount }, () => []);
                            items.forEach((item, idx) => {
                              columns[idx % columnCount].push({
                                item,
                                globalIdx: sectionStartIdx + idx,
                              });
                            });
                  return columns.map((col, colIdx) => (
                    <div
                      key={`col-${colIdx}`}
                      className="flex-1 min-w-0 flex flex-col gap-3"
                    >
                      {col.map(({ item, globalIdx: idx }) => {
                    if (item.kind === 'skeleton') {
                      // Skeleton % — driven by elapsed time since the
                      // brand-setup job started. All in-flight skeletons
                      // share the same %; not per-tile (we don't know
                      // per-slot start times — they fire in parallel).
                      const skelPct = activeJobStartedAt
                        ? Math.round(computeProgress(activeJobStartedAt))
                        : 0;
                      return (
                        <div
                          key={item.key}
                          className="relative w-full aspect-square rounded-xl bg-white border border-surface-dark/40 overflow-hidden"
                        >
                          <div className="absolute inset-0 bg-gradient-to-br from-surface via-surface-dark/20 to-surface animate-pulse" />
                          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-dark-muted">
                            <i className="ri-loader-4-line text-2xl animate-spin text-primary" />
                            <span className="text-[10px] uppercase tracking-wider font-bold">Rendering</span>
                            {skelPct > 0 && (
                              <span className="text-base font-bold text-primary tabular-nums">
                                {skelPct}%
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                    if (item.kind === 'virtual-locked') {
                      // Visual-only placeholder shown BEFORE the backend's
                      // stub row lands in DB. Looks identical to a real
                      // locked stub (gradient bg + lock icon + Unlock pill)
                      // but click is disabled — once the real row lands via
                      // polling, the real interactive stub takes its place.
                      return (
                        <div
                          key={item.key}
                          className="relative w-full aspect-square rounded-xl border border-surface-dark/40 overflow-hidden bg-gradient-to-br from-surface-dark/40 to-surface-dark/20"
                          aria-label="Locked slot — waiting to be activated"
                          title="Waiting for the backend to register this slot…"
                        >
                          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 text-white bg-dark/30">
                            <i className="ri-lock-line text-3xl" />
                            <span className="px-4 py-1.5 rounded-full bg-white/80 text-primary text-xs font-bold uppercase tracking-wider shadow-md">
                              Unlock
                            </span>
                            <span className="px-2 py-0.5 rounded bg-white/15 text-white/80 text-[10px] font-semibold uppercase tracking-wider">
                              1 credit
                            </span>
                          </div>
                        </div>
                      );
                    }
                    const post = item.post;
                    return (() => {
                  const url = imageUrls[post.id];
                  const displayTitle = post.title || post.headline || `Post ${post.id.slice(0, 6)}`;
                  const scheduleStatus = summarisePostSchedule(scheduledByPost[post.id]);
                  // Stub posts (brand-setup batch slots not yet rendered)
                  // have no image_path. They render as locked tiles with a
                  // plain gradient background — the "Unlock" CTA fires the
                  // render on-demand for 1 credit.
                  const isStub = !post.image_path;
                  const isCarousel = !isStub && post.image_path.endsWith('.zip');
                  // Animation-route MP4s (Jun 11). Rendered as a muted
                  // looping <video> tile — autoplay is allowed because
                  // muted; the loop doubles as the preview.
                  const isVideo = !isStub && post.image_path.endsWith('.mp4');
                  // Three distinct lock sources:
                  //   tileLocked  — brand-setup batch flagged this post as
                  //                 locked (first 5 of 20 unlocked by default).
                  //                 Stubs are ALWAYS locked. CTA: 1-credit
                  //                 render via /unlock.
                  //   tilePaywalled — plan-tier paywall (free=4, starter=12,
                  //                   pro/business=∞). CTA: /checkout upgrade.
                  // tileLocked wins when both apply — the user has direct
                  // agency over it (one click to reveal) vs the paywall
                  // (requires a plan upgrade).
                  // A tile with an image is NEVER an unrendered stub — only a
                  // stub (no image_path) can show the 1-credit render "Unlock"
                  // CTA. Gating on isStub (was bare !post.unlocked) fixes the
                  // "rendered tile shows Unlock after navigating back" bug: a
                  // stale/false `unlocked` flag on an already-rendered tile no
                  // longer resurrects the CTA. A rendered-but-paywalled tile
                  // (image present, beyond free cap, non-paid) now correctly
                  // falls through to tilePaywalled (upgrade), not render-unlock.
                  const tileLocked = isStub && !post.unlocked;
                  // Posts generated by a paid batch action (brand-setup,
                  // Ad Strategist, Ad Pilot) bypass the plan-cap paywall.
                  // The user already spent credits to render these tiles —
                  // hiding them behind an Upgrade CTA would be a double charge.
                  // Source-less tiles (manual Save-to-Gallery + Recreate
                  // exports) still respect the cap so the upgrade prompt
                  // continues to drive plan conversions on free saves.
                  const isPaidBatchTile = !!(post.source_job_id || post.source_setup_job_id);
                  const tilePaywalled = !tileLocked && !isPaidBatchTile && idx >= visibleCap;
                  const isLocked = tileLocked || tilePaywalled;
                  const isUnlocking = unlockingIds.has(post.id);
                  // Paid auto-render tile (Generate-20-more top-up): inserted
                  // unlocked because the user already paid for all 20, with no
                  // image yet → actively rendering. Show a spinner, NEVER an
                  // Unlock CTA (nothing to unlock — already paid). Reads DB
                  // state (unlocked + paid-batch + no image), so it survives a
                  // page refresh, unlike the in-memory top-up poll set.
                  const isRendering = isStub && post.unlocked && isPaidBatchTile;
                  // Jun 22 — stuck-render detection. When a tile has been in
                  // RENDERING state for > RENDER_TIMEOUT_MS without completing,
                  // surface a Try-again button instead of the infinite
                  // spinner. The retry calls the unlock endpoint which
                  // re-fires the render from render_config; the credit
                  // reserve is SKIPPED on retry (already paid up-front
                  // for top-up batches). renderTick re-evaluates every
                  // 30s while ANY tile is rendering.
                  const createdAtMs = post.created_at
                    ? new Date(post.created_at).getTime() : 0;
                  // Aug 2 — the stuck-render timer references queued_at (stamped
                  // when the stub is unlocked) when present, so the window is
                  // fresh from queue-time WITHOUT resetting created_at (which
                  // would re-bucket the tile into a new "Today" date section).
                  // Auto-render stubs (never unlocked → no queued_at) fall back
                  // to created_at (≈ batch-setup time, correct for that window).
                  const queuedAtMs = post.queued_at
                    ? new Date(post.queued_at).getTime() : 0;
                  const stuckRefMs = queuedAtMs > 0 ? queuedAtMs : createdAtMs;
                  // Jun 25 — brand-setup first-`activeJobTarget` stubs (by
                  // slot_idx 0..target-1) are auto-rendered upfront by the
                  // worker. They sit at unlocked=false during the ~10-25s
                  // render window, so the isRendering check above (which
                  // requires unlocked=true) misses them. Without this gate
                  // the Unlock CTA shows and a click would DOUBLE-CHARGE the
                  // user — the unlock endpoint reserves + renders the same
                  // stub in parallel with the worker. Gracefully falls back
                  // to the Unlock CTA after RENDER_TIMEOUT_MS so a stuck
                  // worker doesn't lock the user out of manual retry.
                  const isAutoRendering =
                    isStub
                    && !post.unlocked
                    && isFromActiveJob(post)
                    && activeJobTarget > 0
                    && (post.slot_idx ?? Infinity) < activeJobTarget
                    // Jul 14 — a backend-confirmed failure (render_error) drops
                    // the auto-render state immediately so the Unlock CTA shows,
                    // no 5-min wait.
                    && !post.render_failed
                    && !(stuckRefMs > 0 && (nowMs - stuckRefMs) > RENDER_TIMEOUT_MS);
                  // Jul 14 — "Try again" the moment the backend gives up
                  // (render_config.render_error → post.render_failed), else fall
                  // back to the 5-min client stuck-timeout for a silently-dead
                  // worker that never stamped an error.
                  const isStuck = isRendering
                    && (post.render_failed
                        || (stuckRefMs > 0 && (nowMs - stuckRefMs) > RENDER_TIMEOUT_MS));
                  return (
                    <div
                      key={post.id}
                      role="button"
                      tabIndex={0}
                      aria-label={
                        tileLocked ? `${displayTitle} — locked`
                        : tilePaywalled ? `${displayTitle} — upgrade required`
                        : `Preview ${displayTitle}`
                      }
                      title={
                        tileLocked ? 'Click to unlock'
                        : tilePaywalled ? 'Upgrade to unlock'
                        : displayTitle
                      }
                      onClick={() => {
                        if (isRendering || isAutoRendering) return; // still rendering — no detail yet, no Unlock
                        if (tileLocked) { handleUnlock(post.id); return; }
                        if (tilePaywalled) { navigate('/checkout'); return; }
                        // Tile click → editor on desktop, view-only modal on
                        // mobile (see handleTileOpen).
                        handleTileOpen(post);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        if (isRendering || isAutoRendering) return;
                        if (tileLocked) { handleUnlock(post.id); return; }
                        if (tilePaywalled) { navigate('/checkout'); return; }
                        handleTileOpen(post);
                      }}
                      className={`group relative w-full rounded-xl border border-surface-dark/40 overflow-hidden transition-all hover:shadow-lg hover:shadow-primary/10 hover:border-primary/40 cursor-pointer ${
                        aspectClass(post.aspect_ratio ?? '1:1')
                      } ${isStub
                          ? 'bg-gradient-to-br from-surface-dark/40 to-surface-dark/20'
                          : 'bg-white bg-surface'
                        }`}
                    >
                      {tileLocked && (
                        <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 text-white ${
                          isStub
                            // Stub: no image underneath, so skip backdrop-blur
                            // (it'd blur the gradient and look fuzzy). Solid
                            // dim overlay reads as "empty slot".
                            ? 'bg-dark/30'
                            // Already-rendered: blur the image underneath.
                            : 'backdrop-blur-md bg-dark/40'
                        }`}>
                          {(isUnlocking || isAutoRendering) ? (
                            // Per-tile loader (Jun 24 update — unify visual).
                            // After the Jun 24 worker-pool change in
                            // processUnlockQueue, up to UNLOCK_CONCURRENCY
                            // (=5) tiles render in parallel; the other
                            // 10 wait in the queue. We label ALL of them
                            // "Generating…" (matches "Generate 20 more"
                            // UX) — only the actively-rendering 5 also
                            // show a %, so the user can still tell which
                            // ones the backend is currently working.
                            //
                            // Differentiator: presence of a start time
                            // in unlockStartTimes — set by _processOne
                            // at render-start, never at enqueue. Avoids
                            // the bug where all N tiles tick up to 92%
                            // in parallel even though only 5 are
                            // actually rendering.
                            //
                            // Jun 25 — same loader covers `isAutoRendering`
                            // (brand-setup first-5 worker auto-render).
                            // Those stubs have no unlockStartTimes entry
                            // (never enqueued through processUnlockQueue)
                            // so they fall through to spinner + label only,
                            // no %. Matches the user's mental model of
                            // "the first batch is generating in the
                            // background."
                            (() => {
                              const start = unlockStartTimes.get(post.id);
                              const isActivelyRendering = start !== undefined && start > 0;
                              return (
                                <>
                                  <i className="ri-loader-4-line text-3xl animate-spin" />
                                  {isActivelyRendering && (
                                    <span className="text-2xl font-bold tabular-nums">
                                      {Math.round(computeProgress(start))}%
                                    </span>
                                  )}
                                  <span className="px-4 py-1.5 rounded-full bg-white text-primary text-xs font-bold uppercase tracking-wider shadow-md">
                                    Generating…
                                  </span>
                                </>
                              );
                            })()
                          ) : (
                            <>
                              <i className="ri-lock-line text-3xl" />
                              <span className="px-4 py-1.5 rounded-full bg-white text-primary text-xs font-bold uppercase tracking-wider shadow-md">
                                Unlock
                              </span>
                              {isStub && (
                                <span className="px-2 py-0.5 rounded bg-white/15 text-white/80 text-[10px] font-semibold uppercase tracking-wider">
                                  1 credit
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      {tilePaywalled && (
                        <div className="absolute inset-0 z-10 backdrop-blur-md bg-dark/30 flex flex-col items-center justify-center gap-2 text-white">
                          <i className="ri-lock-line text-2xl" />
                          <span className="text-xs font-bold uppercase tracking-wider">Upgrade</span>
                        </div>
                      )}
                      {/* Paid auto-render (top-up) — actively rendering, no
                          Unlock CTA. Matches the active-section "Rendering"
                          tile so the 20 read as queued + filling in one by one. */}
                      {isRendering && !isStuck && (
                        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 text-primary">
                          <i className="ri-loader-4-line text-3xl animate-spin" />
                          <span className="px-3 py-1 rounded-full bg-white text-primary text-[10px] font-bold uppercase tracking-wider shadow-md">
                            Rendering…
                          </span>
                        </div>
                      )}
                      {isStuck && (
                        <div
                          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-surface-dark/40 backdrop-blur-sm"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex flex-col items-center gap-1 text-center px-4">
                            <i className="ri-time-line text-3xl text-dark-muted" />
                            <span className="text-xs font-bold text-dark">Render took too long</span>
                            <span className="text-[10px] text-dark-muted">Try again — no extra credits</span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnlock(post.id);
                            }}
                            disabled={isUnlocking}
                            className="px-4 py-1.5 rounded-full bg-primary text-white text-xs font-bold shadow-md hover:bg-primary-hover disabled:opacity-50 cursor-pointer"
                          >
                            {isUnlocking
                              ? <><i className="ri-loader-4-line animate-spin" /> Retrying…</>
                              : <><i className="ri-refresh-line mr-1" />Try again</>}
                          </button>
                        </div>
                      )}
                      {/* Image — carousels render a 2x2 frame collage from
                          the zip; single images render normally. */}
                      {isCarousel && url ? (
                        <CarouselThumbnail
                          zipUrl={url}
                          alt={displayTitle}
                          className="absolute inset-0 w-full h-full"
                        />
                      ) : isVideo && url ? (
                        <video
                          src={url}
                          muted
                          loop
                          autoPlay
                          playsInline
                          preload="metadata"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      ) : url ? (
                        // Skeleton placeholder until the image decodes, then
                        // it fades in (Jun 19). No loading="lazy" — the
                        // section's opacity-reveal animation stops Chrome's
                        // lazy observer from firing (Gotcha #277); ThumbImage
                        // is eager by default.
                        <ThumbImage src={url} alt={displayTitle} />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <i className="ri-image-line text-dark-muted/40 text-3xl" />
                        </div>
                      )}

                      {/* ★ Starred toggle — always visible (mobile-tappable),
                          bottom-right. Semi-transparent outline when off,
                          solid amber when starred. Drop-shadow keeps it legible
                          on any image. Only on real rendered tiles. */}
                      {!isLocked && !isRendering && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleStar(post); }}
                          title={post.starred ? 'Starred — tap to remove' : 'Star this ad'}
                          aria-label={post.starred ? 'Remove star' : 'Star this ad'}
                          aria-pressed={post.starred}
                          className="absolute bottom-2 right-2 z-20 w-8 h-8 flex items-center justify-center cursor-pointer"
                        >
                          <i
                            style={{ WebkitTextStroke: '1.5px white', paintOrder: 'stroke' }}
                            className={`text-xl transition-all ${post.starred ? 'ri-star-fill text-primary' : 'ri-star-line text-white'}`}
                          />
                        </button>
                      )}

                      {/* Red diagnostic cost pill — top-left. Surfaces the
                          actual USD cost per render so we can reconcile
                          against Cloud Billing. Hidden when null (historical
                          posts). Same stacking-context behavior as the date
                          chip — dims under the hover overlay on hover. */}
                      {/* Diagnostic only — hidden in production (kept for local
                          cost reconciliation). */}
                      {import.meta.env.DEV && (
                        <CostPill cost={post.generation_cost_usd} provider={post.generation_provider} />
                      )}

                      {/* Product-shot chip (Jun 23) — emerald badge on tiles
                          rendered through the is_product_shot recipe path so
                          the curator can spot them on /gallery without
                          opening each post. Sits below the cost pill in the
                          top-left stack. */}
                      {post.generation_provider === 'openai-product-shot' && (
                        <span
                          className="absolute top-11 left-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-500 text-white text-[10px] font-bold tracking-wide shadow-sm"
                          title="Rendered through a curator-tagged is_product_shot recipe (verbatim, no copy)."
                        >
                          <span aria-hidden>📦</span>
                          <span>PRODUCT SHOT</span>
                        </span>
                      )}

                      {/* Status pills — stack at the bottom. Publication
                          pill (from Post Now) above Schedule pill (from
                          calendar). Each only renders when relevant. */}
                      {((post.published_to && post.published_to.length > 0) ||
                        scheduleStatus.kind !== 'none') && (
                        <div className="absolute bottom-2 left-2 right-11 flex flex-col gap-1 items-start">
                          {post.published_to && post.published_to.length > 0 && (
                            <PublicationPill publications={post.published_to} />
                          )}
                          {scheduleStatus.kind !== 'none' && (
                            <ScheduleStatusPill status={scheduleStatus} />
                          )}
                        </div>
                      )}

                      {/* Hover overlay — dim image + reveal action buttons.
                          Pointer-events-none on the dim layer so it doesn't
                          intercept clicks on the buttons. Hidden entirely
                          on locked / paywalled tiles — those tiles have
                          their own primary action (Unlock / Upgrade) and
                          shouldn't expose download/post/schedule. */}
                      {!isLocked && !isRendering && (
                      <div className="hidden lg:block absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                      )}

                      {/* Hover action cluster — DESKTOP ONLY. On touch there's no
                          hover, so these live in the tap-to-open modal instead
                          (see the previewPost modal's action row). */}
                      {!isLocked && !isRendering && (
                      <div className="hidden lg:flex absolute inset-0 items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDownload(post); }}
                          title="Download"
                          aria-label="Download"
                          className="pointer-events-auto w-10 h-10 rounded-full bg-white hover:bg-primary hover:text-white text-dark shadow-md flex items-center justify-center cursor-pointer transition-colors"
                        >
                          <i className="ri-download-line text-base" />
                        </button>
                        {/* Post to Facebook — Facebook-blue hover. Disabled
                            only when no FB connection exists. Carousels now
                            go through the multi-photo /feed endpoint
                            (backend unzips + uploads each frame unpublished
                            + combines via attached_media). */}
                        {SOCIAL_PUBLISHING_ENABLED && (() => {
                          const fbDisabled = !fbStatus?.connected;
                          const fbTitle = !fbStatus?.connected
                            ? 'Connect Facebook in the sidebar first'
                            : `Post to ${fbStatus.pageName || 'Facebook'}`;
                          return (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!fbDisabled) handlePostToSocial(post, 'facebook');
                              }}
                              disabled={fbDisabled}
                              title={fbTitle}
                              aria-label="Post to Facebook"
                              className="pointer-events-auto w-10 h-10 rounded-full bg-white hover:bg-[#1877F2] hover:text-white text-[#1877F2] shadow-md flex items-center justify-center cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#1877F2]"
                            >
                              <i className="ri-facebook-circle-line text-base" />
                            </button>
                          );
                        })()}
                        {/* Post to Instagram — IG-pink. Requires (a) FB
                            connection AND (b) FB Page has IG linked.
                            Carousels now go through the 3-stage IG
                            container API (child containers → carousel
                            container → media_publish). */}
                        {SOCIAL_PUBLISHING_ENABLED && (() => {
                          const igConnected = !!fbStatus?.instagramUsername;
                          const igDisabled = !igConnected;
                          const igTitle = !fbStatus?.connected
                            ? 'Connect Facebook first — Instagram is discovered via the FB Page'
                            : !igConnected
                              ? 'Link Instagram to your Facebook Page, then reconnect FB here'
                              : `Post to @${fbStatus.instagramUsername}`;
                          return (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!igDisabled) handlePostToSocial(post, 'instagram');
                              }}
                              disabled={igDisabled}
                              title={igTitle}
                              aria-label="Post to Instagram"
                              className="pointer-events-auto w-10 h-10 rounded-full bg-white hover:bg-[#E4405F] hover:text-white text-[#E4405F] shadow-md flex items-center justify-center cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#E4405F]"
                            >
                              <i className="ri-instagram-line text-base" />
                            </button>
                          );
                        })()}
                        {SCHEDULING_ENABLED && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            // If the post already has an upcoming schedule,
                            // open the modal in EDIT mode with the full
                            // cohort (every platform sharing the next-
                            // upcoming scheduled_at). User toggles to add
                            // or remove platforms, moves time, or cancels.
                            // Other-timestamp tiles for the same post stay
                            // in /schedule for individual management.
                            const cohort = editCohortTiles(scheduledByPost[post.id]);
                            setScheduling({ post, editingTiles: cohort });
                          }}
                          title={
                            summarisePostSchedule(scheduledByPost[post.id]).kind === 'scheduled'
                              ? 'Edit schedule'
                              : 'Schedule'
                          }
                          aria-label="Schedule"
                          className="pointer-events-auto w-10 h-10 rounded-full bg-white hover:bg-primary hover:text-white text-dark shadow-md flex items-center justify-center cursor-pointer transition-colors"
                        >
                          <i className="ri-calendar-event-line text-base" />
                        </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteId(post.id); }}
                          title="Delete"
                          aria-label="Delete"
                          className="pointer-events-auto w-10 h-10 rounded-full bg-white hover:bg-red-500 hover:text-white text-red-600 shadow-md flex items-center justify-center cursor-pointer transition-colors"
                        >
                          <i className="ri-delete-bin-line text-base" />
                        </button>
                      </div>
                      )}
                    </div>
                    );
                    })();
                  })}
                    </div>
                  ));
                          })()}
                        </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.section>
                    );
                  });
                })()}
              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={handleLoadOlder}
                    disabled={loadingOlder}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-surface-dark/40 text-sm font-semibold text-dark hover:border-primary/50 hover:text-primary hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loadingOlder ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Loading older creatives…
                      </>
                    ) : (
                      <>
                        <i className="ri-history-line" />
                        Load older creatives
                      </>
                    )}
                  </button>
                </div>
              )}
              </div>
              </LayoutGroup>
            )}
        </div>
      </div>

      {/* Schedule modal — opens from a card's Schedule button. Two modes:
          - editingTiles=[]      → create mode (fresh schedule).
          - editingTiles=[…]     → edit mode (cohort of tiles sharing one
                                    timestamp). User toggles platforms,
                                    changes time, or cancels. */}
      {scheduling !== null && (
        <ScheduleModal
          editingTiles={scheduling.editingTiles.length > 0 ? scheduling.editingTiles : null}
          createDay={new Date()}
          posts={[scheduling.post]}
          fbStatus={fbStatus}
          onClose={() => setScheduling(null)}
          onSaved={() => {
            const wasEditing = scheduling.editingTiles.length > 0;
            setScheduling(null);
            void fetchPosts();
            setToast({
              message: wasEditing ? 'Schedule updated' : 'Scheduled',
              type: 'success',
            });
          }}
        />
      )}

      {/* Post-to-social modal — unified across single-channel hover icons
          + multi-channel entry points (Jun 17). The hover button on a
          gallery tile passes its channel via initialEnabled; the modal
          renders BOTH channels but only the clicked one is pre-toggled
          on. User can toggle the other channel on to cross-post in the
          same submit. */}
      {socialModal !== null && (() => {
        const { post: modalPost, channel } = socialModal;
        const headerLabel = channel === 'instagram'
          ? `Post${fbStatus?.instagramUsername ? ` from @${fbStatus.instagramUsername}` : ''}`
          : `Post${fbStatus?.pageName ? ` from ${fbStatus.pageName}` : ''}`;
        return (
          <PostToSocialModal
            post={modalPost}
            initialEnabled={[channel]}
            headerLabel={headerLabel}
            onCaptionSaved={(ch, cap) => {
              // Update the in-memory posts[] so re-opening this post's
              // FB/IG modal reads the just-saved draft from cache instead
              // of seeing stale post.captions === {} and firing a fresh
              // AI suggest call. Backend has already persisted (or is
              // about to via fire-and-forget on close) — this is the
              // mirror image of the row update.
              setPosts((prev) =>
                prev.map((p) =>
                  p.id === modalPost.id
                    ? { ...p, captions: { ...p.captions, [ch]: cap } }
                    : p,
                ),
              );
            }}
            onClose={() => setSocialModal(null)}
            onSuccess={(channelsPublished, submittedMode) => {
              const names = channelsPublished
                .map((c) =>
                  c === 'instagram' ? 'Instagram'
                    : c === 'facebook' ? 'Facebook'
                    : c === 'tiktok' ? 'TikTok'
                    : 'X',
                )
                .join(' + ');
              // Video posts go through the async publish_jobs worker —
              // the "Posted · Channel" pill takes 30s-5min to land. Toast
              // copy reflects the in-flight state so the user isn't
              // confused when the pill doesn't appear instantly.
              const isVideoModal = modalPost.image_path.endsWith('.mp4');
              let message: string;
              if (submittedMode === 'schedule') {
                message = `Scheduled for ${names}`;
              } else if (isVideoModal) {
                message = `Publishing to ${names} — usually 30-90s. ` +
                  `You can keep working; the pill appears when it's live.`;
              } else {
                message = `Posted to ${names}`;
              }
              setToast({ message, type: 'success' });
              // Re-fetch posts so the new "Posted · Channel" pill renders
              // on the tile without a manual reload. For video posts the
              // pill won't appear yet (worker hasn't finished) — the
              // onVideoJobsSubmitted poll loop below covers that.
              void fetchPosts(true);
            }}
            onVideoJobsSubmitted={(jobs) => {
              // Add to the in-flight publish-jobs map so the poll effect
              // tracks them. SessionStorage so they survive nav within
              // /gallery; cleared on terminal status.
              setPublishJobs((prev) => {
                const next = [...prev];
                for (const j of jobs) {
                  if (!next.find((x) => x.jobId === j.jobId)) next.push(j);
                }
                return next;
              });
            }}
          />
        );
      })()}

      {/* "Generate 20 more" — product picker modal. Product selection is the
          ONLY input; on Generate it spins up an Ad Strategist top-up run. */}
      {topUpPickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark/40 backdrop-blur-sm"
          onClick={() => { if (!topUpSubmitting) setTopUpPickerOpen(false); }}
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-surface-dark/40">
              <div className="min-w-0">
                <h3 className="font-display font-bold text-dark text-lg leading-tight">
                  Generate Ads
                </h3>
                <p className="text-xs text-dark-muted mt-0.5">
                  Pick the products to feature. Ad Strategist drafts &amp;
                  renders {topUpCount} fresh ads.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { if (!topUpSubmitting) setTopUpPickerOpen(false); }}
                className="w-9 h-9 rounded-full hover:bg-surface flex items-center justify-center text-dark-muted flex-shrink-0"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {/* Jun 26 — includeAllToggle=false so the picker shows the
                  product grid directly. The prior default (All Products
                  toggle ON) was misleading: it set selectedIds=[] +
                  allSelected=true, but the Generate button's gate
                  (`topUpSelectedIds.length === 0 → disabled`) only read
                  selectedIds and ignored allSelected, so users had to
                  toggle off + manually pick before they could submit. */}
              <ProductCatalogPicker
                includeAllToggle={false}
                onSelectionChange={(s: ProductPickerSelection) =>
                  setTopUpSelectedIds(s.selectedIds)
                }
              />
            </div>
            {/* Jul 14 — batch size. 5 / 10 / 20 ads (default 20). 1 credit each. */}
            <div className="px-6 py-3 border-t border-surface-dark/40 flex items-center gap-3 flex-wrap">
              <span className="text-xs font-bold text-dark uppercase tracking-wider">How many</span>
              <div className="flex gap-1.5">
                {([5, 10, 20] as const).map((n) => {
                  const isActive = topUpCount === n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setTopUpCount(n)}
                      className={
                        `px-3 py-1.5 rounded-full text-xs font-bold border transition-colors cursor-pointer ` +
                        (isActive
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-dark border-surface-dark/40 hover:border-primary/40')
                      }
                    >
                      {n} ads
                    </button>
                  );
                })}
              </div>
            </div>
            {/* Jun 22 — aspect-ratio picker. All ads in this batch render
                at the chosen aspect. Backend gpt-image-2 handles all 4
                natively (1:1 / 2:3 / 9:16 / 16:9). */}
            <div className="px-6 py-3 border-t border-surface-dark/40 flex items-center gap-3 flex-wrap">
              <span className="text-xs font-bold text-dark uppercase tracking-wider">Format</span>
              <div className="flex gap-1.5">
                {[
                  { id: '1:1', label: 'Square', dims: '1:1' },
                  { id: '4:5', label: 'Portrait', dims: '4:5' },
                  { id: '9:16', label: 'Story', dims: '9:16' },
                  { id: '16:9', label: 'Landscape', dims: '16:9' },
                ].map((opt) => {
                  const isActive = topUpAspectRatio === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setTopUpAspectRatio(opt.id)}
                      className={
                        `px-3 py-1.5 rounded-full text-xs font-bold border transition-colors cursor-pointer ` +
                        (isActive
                          ? 'bg-primary text-white border-primary'
                          : 'bg-white text-dark border-surface-dark/40 hover:border-primary/40')
                      }
                      title={opt.dims}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 px-6 py-4 border-t border-surface-dark/40 bg-surface/50">
              <span className="text-xs text-dark-muted">
                {topUpSelectedIds.length} product
                {topUpSelectedIds.length === 1 ? '' : 's'} selected · {topUpAspectRatio} · {topUpCount} credits
              </span>
              {balance !== null && balance < topUpCount ? (
                <button
                  type="button"
                  onClick={() => navigate('/checkout')}
                  className="w-full sm:w-auto justify-center whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-amber-500 text-white text-sm font-bold shadow-md hover:bg-amber-600 transition-colors"
                >
                  <i className="ri-lock-line" />
                  Need {topUpCount} credits — Upgrade
                </button>
              ) : (
                <button
                  type="button"
                  disabled={topUpSubmitting || topUpSelectedIds.length === 0}
                  onClick={handleTopUpGenerate}
                  className="w-full sm:w-auto justify-center whitespace-nowrap inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold shadow-md shadow-primary/25 hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {topUpSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" /> Starting…
                    </>
                  ) : (
                    <>
                      <i className="ri-sparkling-2-line" /> Generate {topUpCount} ads · {topUpCount} credits
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl border border-surface-dark/40 p-6 max-w-sm w-full shadow-xl">
            <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="font-display font-bold text-dark text-lg mb-2">Delete this post?</h3>
            <p className="text-dark-muted text-sm mb-6">
              This permanently removes the saved image. You can&apos;t undo this.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteId(null)}
                className="flex-1 px-4 py-2.5 rounded-full border border-dark/10 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleConfirmDelete(deleteId)}
                className="flex-1 px-4 py-2.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-bold transition-all cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk-unlock confirm modal (Jun 17). Asks before charging N
          credits to fan out N parallel renders. Click backdrop or
          Cancel to dismiss. */}
      {bulkUnlockTarget !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-dark/40 backdrop-blur-sm p-4"
          onClick={() => !bulkUnlocking && setBulkUnlockTarget(null)}
        >
          <div
            className="bg-white rounded-2xl border border-surface-dark/40 p-6 max-w-sm w-full shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {balance !== null && balance < bulkUnlockTarget.lockedCount ? (
              // Not enough credits → prompt to upgrade instead of unlock.
              <>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <i className="ri-vip-crown-line text-primary text-xl" />
                </div>
                <h3 className="font-display font-bold text-dark text-lg mb-2">
                  {balance === 0 ? "You're out of credits" : 'Not enough credits'}
                </h3>
                <p className="text-dark-muted text-sm mb-6">
                  Unlocking {bulkUnlockTarget.lockedCount} ad{bulkUnlockTarget.lockedCount === 1 ? '' : 's'} costs{' '}
                  <span className="font-bold text-dark">{bulkUnlockTarget.lockedCount} credit{bulkUnlockTarget.lockedCount === 1 ? '' : 's'}</span>, but you
                  have <span className="font-bold text-dark">{balance}</span>. Upgrade your plan to unlock these and keep creating.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setBulkUnlockTarget(null)}
                    className="flex-1 px-4 py-2.5 rounded-full border border-dark/10 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => { setBulkUnlockTarget(null); navigate('/checkout'); }}
                    className="flex-1 px-4 py-2.5 rounded-full bg-primary hover:bg-primary/90 text-white text-sm font-bold transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-vip-crown-line" />
                    Upgrade
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <i className="ri-flashlight-line text-primary text-xl" />
                </div>
                <h3 className="font-display font-bold text-dark text-lg mb-2">
                  Unlock all {bulkUnlockTarget.lockedCount} remaining ads?
                </h3>
                <p className="text-dark-muted text-sm mb-6">
                  Renders {bulkUnlockTarget.lockedCount} ad{bulkUnlockTarget.lockedCount === 1 ? '' : 's'} in parallel —
                  they&apos;ll appear in My Creatives one by one as each finishes.
                  Cost: <span className="font-bold text-dark">{bulkUnlockTarget.lockedCount} credit{bulkUnlockTarget.lockedCount === 1 ? '' : 's'}</span>.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setBulkUnlockTarget(null)}
                    disabled={bulkUnlocking}
                    className="flex-1 px-4 py-2.5 rounded-full border border-dark/10 text-dark text-sm font-semibold hover:bg-surface transition-all cursor-pointer disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBulkUnlock}
                    disabled={bulkUnlocking}
                    className="flex-1 px-4 py-2.5 rounded-full bg-primary hover:bg-primary/90 text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {bulkUnlocking ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" />
                        Queuing…
                      </>
                    ) : (
                      <>Unlock all</>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Full-res preview modal. Click backdrop or press Escape to close.
          Image fits within viewport; download button stays accessible. */}
      {previewPost && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-dark/80 backdrop-blur-sm p-4"
          onClick={() => setPreviewPost(null)}
        >
          <button
            onClick={() => setPreviewPost(null)}
            aria-label="Close preview"
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 hover:bg-white text-dark shadow-lg flex items-center justify-center cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[92vh] overflow-y-auto flex flex-col items-center gap-3"
          >
            {previewLoading || !previewUrl ? (
              <div className="w-24 h-24 flex items-center justify-center text-white">
                <i className="ri-loader-4-line text-3xl animate-spin" />
              </div>
            ) : (
              <img
                src={previewUrl}
                alt={previewPost.title || ''}
                className="max-w-full max-h-[62vh] object-contain rounded-xl shadow-2xl"
              />
            )}
            {previewEditNotice && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 text-dark text-xs font-semibold shadow-md">
                <i className="ri-computer-line text-primary" />
                Open on the desktop version to make edits.
              </div>
            )}
            {/* Action row — the tile hover-cluster actions relocated here for
                touch (hover is meaningless on mobile). Opening the social /
                schedule / delete surfaces closes this modal first so nothing
                stacks. Download keeps the modal open. */}
            {previewUrl && (
              <div className="flex flex-wrap items-start justify-center gap-4 pt-1 pb-1">
                <button
                  type="button"
                  onClick={() => previewPost && handleDownload(previewPost)}
                  aria-label="Download"
                  className="flex flex-col items-center gap-1.5 cursor-pointer group/act"
                >
                  <span className="w-12 h-12 rounded-full bg-white text-dark shadow-md flex items-center justify-center group-hover/act:bg-primary group-hover/act:text-white transition-colors">
                    <i className="ri-download-line text-lg" />
                  </span>
                  <span className="text-white text-xs font-semibold">Download</span>
                </button>
                {SOCIAL_PUBLISHING_ENABLED && (
                  <button
                    type="button"
                    onClick={() => { const p = previewPost; if (!p) return; setPreviewPost(null); handlePostToSocial(p, 'facebook'); }}
                    aria-label="Post to Facebook"
                    className="flex flex-col items-center gap-1.5 cursor-pointer group/act"
                  >
                    <span className="w-12 h-12 rounded-full bg-white text-[#1877F2] shadow-md flex items-center justify-center group-hover/act:bg-[#1877F2] group-hover/act:text-white transition-colors">
                      <i className="ri-facebook-circle-line text-lg" />
                    </span>
                    <span className="text-white text-xs font-semibold">Facebook</span>
                  </button>
                )}
                {SOCIAL_PUBLISHING_ENABLED && (
                  <button
                    type="button"
                    onClick={() => { const p = previewPost; if (!p) return; setPreviewPost(null); handlePostToSocial(p, 'instagram'); }}
                    aria-label="Post to Instagram"
                    className="flex flex-col items-center gap-1.5 cursor-pointer group/act"
                  >
                    <span className="w-12 h-12 rounded-full bg-white text-[#E4405F] shadow-md flex items-center justify-center group-hover/act:bg-[#E4405F] group-hover/act:text-white transition-colors">
                      <i className="ri-instagram-line text-lg" />
                    </span>
                    <span className="text-white text-xs font-semibold">Instagram</span>
                  </button>
                )}
                {SCHEDULING_ENABLED && (
                  <button
                    type="button"
                    onClick={() => {
                      const p = previewPost;
                      if (!p) return;
                      const cohort = editCohortTiles(scheduledByPost[p.id]);
                      setPreviewPost(null);
                      setScheduling({ post: p, editingTiles: cohort });
                    }}
                    aria-label="Schedule"
                    className="flex flex-col items-center gap-1.5 cursor-pointer group/act"
                  >
                    <span className="w-12 h-12 rounded-full bg-white text-dark shadow-md flex items-center justify-center group-hover/act:bg-primary group-hover/act:text-white transition-colors">
                      <i className="ri-calendar-event-line text-lg" />
                    </span>
                    <span className="text-white text-xs font-semibold">Schedule</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { const p = previewPost; if (!p) return; setPreviewPost(null); setDeleteId(p.id); }}
                  aria-label="Delete"
                  className="flex flex-col items-center gap-1.5 cursor-pointer group/act"
                >
                  <span className="w-12 h-12 rounded-full bg-white text-red-600 shadow-md flex items-center justify-center group-hover/act:bg-red-500 group-hover/act:text-white transition-colors">
                    <i className="ri-delete-bin-line text-lg" />
                  </span>
                  <span className="text-white text-xs font-semibold">Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayoutV2>
  );
}
